import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import * as yaml from 'js-yaml';
import CodeMirror from '@uiw/react-codemirror';
import { yaml as yamlLanguage } from '@codemirror/lang-yaml';
import { linter, Diagnostic } from '@codemirror/lint';
import {
    Save,
    Plus,
    Trash2,
    Edit2,
    X,
    FileText,
    ChevronRight,
    ChevronDown,
    AlertCircle,
    CheckCircle2,
    Loader2,
    Search,
    GripVertical
} from 'lucide-react';
import {
    DndContext,
    closestCenter,
    KeyboardSensor,
    PointerSensor,
    useSensor,
    useSensors,
    DragEndEvent
} from '@dnd-kit/core';
import {
    arrayMove,
    SortableContext,
    sortableKeyboardCoordinates,
    useSortable,
    verticalListSortingStrategy
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { guidelinesApi } from '../services/api';
import {
    GuidelineRule,
    GuidelineItem,
    Guideline,
    GuidelineVersionInfo,
    GuidelineRegularExpressionCondition,
    GuidelineResponse,
    UpdateGuidelineRequest,
    UnfixableDomainRule,
} from '../types/guideline';
import UnfixableRulesSection from '../components/guideline/UnfixableRulesSection';
import {
    buildUnfixableRulesExportFilename,
    sortUnfixableRulesBySourceRow,
} from '../utils/unfixableRulesExcel';
import './GuidelineManagementPage.css';

const computeLineStarts = (text: string): number[] => {
    const starts = [0];
    for (let i = 0; i < text.length; i++) {
        if (text[i] === '\n') starts.push(i + 1);
    }
    return starts;
};

const lineColToPos = (lineStarts: number[], line: number, col: number): number => {
    const safeLine = Math.max(0, Math.min(line, lineStarts.length - 1));
    return Math.min((lineStarts[safeLine] ?? 0) + Math.max(0, col), (lineStarts[lineStarts.length - 1] ?? 0) + 1000000);
};

/** Trim ASCII whitespace and Japanese full-width space (U+3000) from start and end of a string. */
const trimPromptAndText = (value: string | null | undefined): string => {
    if (value == null) return '';
    const s = String(value);
    return s.replace(/^[\s\u3000]+|[\s\u3000]+$/g, '');
};

const parseOptionalNumber = (value: any): number | undefined => {
    if (value === undefined || value === null || value === '') return undefined;
    if (typeof value === 'number') return Number.isFinite(value) ? value : undefined;
    if (typeof value === 'string') {
        const n = Number(value.trim());
        return Number.isFinite(n) ? n : undefined;
    }
    return undefined;
};

const parseOptionalInt = (value: any): number | undefined => {
    const n = parseOptionalNumber(value);
    if (n === undefined) return undefined;
    return Math.trunc(n);
};

/** Find all lines where type: is set to an invalid value (not in valid set). Returns ranges for the whole line. */
const findInvalidTypeLineRanges = (text: string): Array<{ from: number; to: number; value: string }> => {
    const results: Array<{ from: number; to: number; value: string }> = [];
    const lineStarts = computeLineStarts(text);
    const lines = text.split(/\n/);
    const validTypes = new Set([
        'text_check',
        'forbidden_regular_expression_check',
        'musthave_regular_expression_check',
        'forbidden_image',
        'required_image_match',
        'description',
    ]);
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const lineStart = lineStarts[i] ?? 0;
        const lineEnd = i + 1 < lineStarts.length ? (lineStarts[i + 1]! - 1) : text.length;
        const typeMatch = line.match(/^\s*type\s*:\s*(.+)$/);
        if (!typeMatch) continue;
        const rawValue = typeMatch[1].trim();
        const value = rawValue.replace(/^["']|["']$/g, '').trim();
        if (value && !validTypes.has(value)) {
            results.push({ from: lineStart, to: lineEnd, value });
        }
    }
    return results;
};

/** Find all lines where patrol_source: is set to an invalid value (not all_page or page_chunk). */
const findInvalidPatrolSourceLineRanges = (text: string): Array<{ from: number; to: number; value: string }> => {
    const results: Array<{ from: number; to: number; value: string }> = [];
    const lineStarts = computeLineStarts(text);
    const lines = text.split(/\n/);
    const validSources = new Set(['all_page', 'page_chunk']);
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const lineStart = lineStarts[i] ?? 0;
        const lineEnd = i + 1 < lineStarts.length ? (lineStarts[i + 1]! - 1) : text.length;
        const match = line.match(/^\s*patrol_source\s*:\s*(.+)$/);
        if (!match) continue;
        const rawValue = match[1].trim();
        const value = rawValue.replace(/^["']|["']$/g, '').trim();
        if (value && !validSources.has(value)) {
            results.push({ from: lineStart, to: lineEnd, value });
        }
    }
    return results;
};

/** Find lines where id: or violation_category: has empty value (for inline editor diagnostics). */
const findEmptyRequiredFieldLineRanges = (
    text: string
): Array<{ from: number; to: number; field: 'id' | 'violation_category' }> => {
    const results: Array<{ from: number; to: number; field: 'id' | 'violation_category' }> = [];
    const lineStarts = computeLineStarts(text);
    const lines = text.split(/\n/);
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const lineStart = lineStarts[i] ?? 0;
        const lineEnd = i + 1 < lineStarts.length ? (lineStarts[i + 1]! - 1) : text.length;
        const idMatch = line.match(/^\s*id\s*:\s*(.*)$/);
        if (idMatch) {
            const raw = (idMatch[1] || '').trim();
            const value = raw.replace(/^["']|["']$/g, '').trim();
            if (value === '') {
                results.push({ from: lineStart, to: lineEnd, field: 'id' });
            }
            continue;
        }
        const catMatch = line.match(/^\s*violation_category\s*:\s*(.*)$/);
        if (catMatch) {
            const raw = (catMatch[1] || '').trim();
            const value = raw.replace(/^["']|["']$/g, '').trim();
            if (value === '') {
                results.push({ from: lineStart, to: lineEnd, field: 'violation_category' });
            }
        }
    }
    return results;
};

/** Find rule blocks with type "forbidden_regular_expression_check" but no forbidden_regular_expressions in the same block. */
const findMissingForbiddenRegexLineRanges = (text: string): Array<{ from: number; to: number }> => {
    const results: Array<{ from: number; to: number }> = [];
    const lineStarts = computeLineStarts(text);
    const lines = text.split(/\n/);
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const typeMatch = line.match(/^\s*type\s*:\s*["']?forbidden_regular_expression_check["']?\s*$/);
        if (!typeMatch) continue;
        const typeIndent = (line.match(/^(\s*)/) || ['', ''])[1].length;
        let found = false;
        for (let j = i + 1; j < lines.length; j++) {
            const next = lines[j];
            const nextIndent = (next.match(/^(\s*)/) || ['', ''])[1].length;
            if (next.trim() !== '' && nextIndent < typeIndent) break;
            if (/^\s*forbidden_regular_expressions\s*:/.test(next)) {
                found = true;
                break;
            }
        }
        if (!found) {
            const lineStart = lineStarts[i] ?? 0;
            const lineEnd = i + 1 < lineStarts.length ? (lineStarts[i + 1]! - 1) : text.length;
            results.push({ from: lineStart, to: lineEnd });
        }
    }
    return results;
};

/** Find rule blocks with type "musthave_regular_expression_check" but no musthave_regular_expressions in the same block. */
const findMissingMusthaveRegexLineRanges = (text: string): Array<{ from: number; to: number }> => {
    const results: Array<{ from: number; to: number }> = [];
    const lineStarts = computeLineStarts(text);
    const lines = text.split(/\n/);
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const typeMatch = line.match(/^\s*type\s*:\s*["']?musthave_regular_expression_check["']?\s*$/);
        if (!typeMatch) continue;
        const typeIndent = (line.match(/^(\s*)/) || ['', ''])[1].length;
        let found = false;
        for (let j = i + 1; j < lines.length; j++) {
            const next = lines[j];
            const nextIndent = (next.match(/^(\s*)/) || ['', ''])[1].length;
            if (next.trim() !== '' && nextIndent < typeIndent) break;
            if (/^\s*musthave_regular_expressions\s*:/.test(next)) {
                found = true;
                break;
            }
        }
        if (!found) {
            const lineStart = lineStarts[i] ?? 0;
            const lineEnd = i + 1 < lineStarts.length ? (lineStarts[i + 1]! - 1) : text.length;
            results.push({ from: lineStart, to: lineEnd });
        }
    }
    return results;
};

/** Find rule blocks with type "text_check" but missing violation_check_prompt or existence_check_prompt in the same block. */
const findMissingTextCheckPromptsLineRanges = (text: string): Array<{ from: number; to: number; message: string }> => {
    const results: Array<{ from: number; to: number; message: string }> = [];
    const lineStarts = computeLineStarts(text);
    const lines = text.split(/\n/);
    for (let i = 0; i < lines.length; i++) {
        const line = lines[i];
        const typeMatch = line.match(/^\s*type\s*:\s*["']?text_check["']?\s*$/);
        if (!typeMatch) continue;
        const typeIndent = (line.match(/^(\s*)/) || ['', ''])[1].length;
        let hasViolation = false;
        let hasExistence = false;
        let skipExistence = false;
        for (let j = i + 1; j < lines.length; j++) {
            const next = lines[j];
            const nextIndent = (next.match(/^(\s*)/) || ['', ''])[1].length;
            if (next.trim() !== '' && nextIndent < typeIndent) break;
            if (/^\s*violation_check_prompt\s*:/.test(next)) hasViolation = true;
            if (/^\s*existence_check_prompt\s*:/.test(next)) hasExistence = true;
            if (/^\s*skip_existence_check\s*:\s*true\s*$/.test(next)) skipExistence = true;
        }
        if (skipExistence) hasExistence = true;
        const lineStart = lineStarts[i] ?? 0;
        const lineEnd = i + 1 < lineStarts.length ? (lineStarts[i + 1]! - 1) : text.length;
        if (!hasViolation && !hasExistence) {
            results.push({ from: lineStart, to: lineEnd, message: 'text_check では violation_check_prompt と existence_check_prompt を指定してください。' });
        } else if (!hasViolation) {
            results.push({ from: lineStart, to: lineEnd, message: 'text_check では violation_check_prompt を指定してください。' });
        } else if (!hasExistence) {
            results.push({ from: lineStart, to: lineEnd, message: 'text_check では existence_check_prompt を指定してください。' });
        }
    }
    return results;
};

const DEFAULT_CHUNK_SETTINGS = {
    chunk_context_before: 1000,
    chunk_context_after: 5000,
    max_chunk_size: 10000,
    chunk_overlap: 500,
    keyword_group_distance: 5000,
};

interface GuidelineYamlMetadata {
    relatedAsps?: string[];
    operationCompanies?: string[];
    mediaNames?: string[];
    chunkSettings?: {
        chunk_context_before?: number;
        chunk_context_after?: number;
        max_chunk_size?: number;
        chunk_overlap?: number;
        keyword_group_distance?: number;
    };
    keywordList?: string[];
    enableSearch?: boolean;
    searchKeywords?: string[];
    enableFiltering?: boolean;
    titleExtractionKeywords?: string[];
    autoExpandKeywords?: string[];
    enableUnfixableClassification?: boolean;
    unfixableByDomain?: UnfixableDomainRule[];
}

/** Unique persisted storage paths for forbidden_image rule assets (excludes localref draft uploads). */
function collectPersistedForbiddenImageFilePaths(items: GuidelineItem[]): string[] {
    const out: string[] = [];
    const seen = new Set<string>();
    for (const item of items || []) {
        const rules = (item as any).rules || [];
        for (const rule of rules) {
            const type = (rule?.type || '') as string;
            const assets =
                type === 'forbidden_image'
                    ? ((rule.forbidden_image_assets || []) as any[])
                    : type === 'required_image_match'
                        ? ((rule.required_image_assets || []) as any[])
                        : [];
            for (const a of assets) {
                const fp = String(a?.file_path || '').trim();
                if (!fp || fp.startsWith('localref://')) continue;
                if (!fp.startsWith('guidelines/')) continue;
                if (seen.has(fp)) continue;
                seen.add(fp);
                out.push(fp);
            }
        }
    }
    return out;
}

/** Ids for categories created only via「カテゴリを追加」; never used by persisted/YAML category ids (those may use `temp_*`). */
const NEW_CLIENT_CATEGORY_ID_PREFIX = 'local_cat_';

const GuidelineManagementPage: React.FC = () => {
    const [guidelines, setGuidelines] = useState<Guideline[]>([]);
    const [selectedGuideline, setSelectedGuideline] = useState<string | null>(null);
    const [guidelineItems, setGuidelineItems] = useState<GuidelineItem[]>([]);
    // Temporary YAML content for editing (only used when in YAML mode)
    const [editingYamlContent, setEditingYamlContent] = useState<string>('');
    const [viewMode, setViewMode] = useState<'yaml' | 'form'>('form');
    // Version management
    const [versions, setVersions] = useState<GuidelineVersionInfo[]>([]);
    const [currentVersion, setCurrentVersion] = useState<number | null>(null);
    const [showVersionDropdown, setShowVersionDropdown] = useState(false);
    const [versionSearchTerm, setVersionSearchTerm] = useState('');
    const versionDropdownRef = useRef<HTMLDivElement>(null);

    const [editingItemId, setEditingItemId] = useState<string | null>(null);
    const [editingItem, setEditingItem] = useState<GuidelineItem | null>(null);
    const [localForbiddenImageFiles, setLocalForbiddenImageFiles] = useState<Record<string, File>>({});
    const localForbiddenImageFilesRef = useRef<Record<string, File>>({});
    const [localForbiddenImagePreviews, setLocalForbiddenImagePreviews] = useState<Record<string, string>>({});
    const [localRequiredImageFiles, setLocalRequiredImageFiles] = useState<Record<string, File>>({});
    const localRequiredImageFilesRef = useRef<Record<string, File>>({});
    const [localRequiredImagePreviews, setLocalRequiredImagePreviews] = useState<Record<string, string>>({});
    const [persistedForbiddenImagePreviews, setPersistedForbiddenImagePreviews] = useState<Record<string, string>>({});
    const [persistedForbiddenImageLoadFailed, setPersistedForbiddenImageLoadFailed] = useState<Record<string, boolean>>({});
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [success, setSuccess] = useState<string | null>(null);
    const [hasChanges, setHasChanges] = useState(false);
    const [showAddGuidelineModal, setShowAddGuidelineModal] = useState(false);
    const [newGuideline, setNewGuideline] = useState<Partial<Guideline>>({ name: '' });
    const [deletingGuidelineId, setDeletingGuidelineId] = useState<string | null>(null);
    const [searchQuery, setSearchQuery] = useState<string>('');
    const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
    const [collapsedRules, setCollapsedRules] = useState<Set<string>>(new Set());
    const [aspOptionsText, setAspOptionsText] = useState<string>('');
    const [operationCompanyOptionsText, setOperationCompanyOptionsText] = useState<string>('');
    const [mediaOptionsText, setMediaOptionsText] = useState<string>('');
    const [chunkContextBefore, setChunkContextBefore] = useState<number>(DEFAULT_CHUNK_SETTINGS.chunk_context_before);
    const [chunkContextAfter, setChunkContextAfter] = useState<number>(DEFAULT_CHUNK_SETTINGS.chunk_context_after);
    const [maxChunkSize, setMaxChunkSize] = useState<number>(DEFAULT_CHUNK_SETTINGS.max_chunk_size);
    const [chunkOverlap, setChunkOverlap] = useState<number>(DEFAULT_CHUNK_SETTINGS.chunk_overlap);
    const [keywordGroupDistance, setKeywordGroupDistance] = useState<number>(DEFAULT_CHUNK_SETTINGS.keyword_group_distance);
    const [keywordListText, setKeywordListText] = useState<string>('');
    const [enableSearch, setEnableSearch] = useState<boolean>(false);
    const [searchKeywordsText, setSearchKeywordsText] = useState<string>('');
    const [enableFiltering, setEnableFiltering] = useState<boolean>(true);
    const [titleExtractionKeywordsText, setTitleExtractionKeywordsText] = useState<string>('');
    const [autoExpandKeywordsText, setAutoExpandKeywordsText] = useState<string>('');
    const [enableUnfixableClassification, setEnableUnfixableClassification] = useState<boolean>(false);
    const [unfixableByDomain, setUnfixableByDomain] = useState<UnfixableDomainRule[]>([]);
    const [unfixableUploadError, setUnfixableUploadError] = useState<string | null>(null);
    const [pendingUnfixableFile, setPendingUnfixableFile] = useState<File | null>(null);
    const listToMultiline = (list: string[]) => (list && list.length > 0 ? list.join('\n') : '');
    const parseMultilineInput = (text: string) =>
        text
            .split('\n')
            .map((line) => line.trim())
            .filter((line) => line.length > 0);

    useEffect(() => {
        localForbiddenImageFilesRef.current = localForbiddenImageFiles;
    }, [localForbiddenImageFiles]);
    useEffect(() => {
        localRequiredImageFilesRef.current = localRequiredImageFiles;
    }, [localRequiredImageFiles]);

    const prefetchPersistedForbiddenImagesForItems = useCallback(async (items: GuidelineItem[]) => {
        const paths = collectPersistedForbiddenImageFilePaths(items);
        if (paths.length === 0) return;
        setPersistedForbiddenImageLoadFailed((prev) => {
            const next = { ...prev };
            for (const p of paths) {
                delete next[p];
            }
            return next;
        });
        const results = await Promise.all(
            paths.map(async (filePath) => {
                try {
                    const blob = await guidelinesApi.getGuidelineAssetBlob(filePath);
                    return { filePath, blob };
                } catch {
                    return { filePath, blob: null as Blob | null };
                }
            })
        );
        setPersistedForbiddenImagePreviews((prev) => {
            const next = { ...prev };
            for (const { filePath, blob } of results) {
                if (!blob || next[filePath]) continue;
                next[filePath] = URL.createObjectURL(blob);
            }
            return next;
        });
        setPersistedForbiddenImageLoadFailed((prev) => {
            const next = { ...prev };
            for (const { filePath, blob } of results) {
                if (!blob) next[filePath] = true;
                else delete next[filePath];
            }
            return next;
        });
    }, []);

    const applyMetadataFromYaml = (metadata?: GuidelineYamlMetadata) => {
        const relatedAsps = metadata?.relatedAsps ?? [];
        const operationCompanies = metadata?.operationCompanies ?? [];
        const mediaNames = metadata?.mediaNames ?? [];
        const keywordList = metadata?.keywordList ?? [];
        const chunkSettings = metadata?.chunkSettings ?? {};

        setAspOptionsText(listToMultiline(relatedAsps));
        setOperationCompanyOptionsText(listToMultiline(operationCompanies));
        setMediaOptionsText(listToMultiline(mediaNames));
        setKeywordListText(listToMultiline(keywordList));

        setEnableSearch(metadata?.enableSearch ?? false);
        setSearchKeywordsText(listToMultiline(metadata?.searchKeywords ?? []));
        setEnableFiltering(metadata?.enableFiltering ?? true);
        setTitleExtractionKeywordsText(listToMultiline(metadata?.titleExtractionKeywords ?? []));
        setAutoExpandKeywordsText(listToMultiline(metadata?.autoExpandKeywords ?? []));
        setEnableUnfixableClassification(metadata?.enableUnfixableClassification ?? false);
        setUnfixableByDomain(sortUnfixableRulesBySourceRow(metadata?.unfixableByDomain ?? []));
        setPendingUnfixableFile(null);
        setUnfixableUploadError(null);

        setChunkContextBefore(chunkSettings.chunk_context_before ?? DEFAULT_CHUNK_SETTINGS.chunk_context_before);
        setChunkContextAfter(chunkSettings.chunk_context_after ?? DEFAULT_CHUNK_SETTINGS.chunk_context_after);
        setMaxChunkSize(chunkSettings.max_chunk_size ?? DEFAULT_CHUNK_SETTINGS.max_chunk_size);
        setChunkOverlap(chunkSettings.chunk_overlap ?? DEFAULT_CHUNK_SETTINGS.chunk_overlap);
        setKeywordGroupDistance(chunkSettings.keyword_group_distance ?? DEFAULT_CHUNK_SETTINGS.keyword_group_distance);
    };

    // Compute YAML from guideline state (single source of truth)
    const yamlContent = useMemo(() => {
        return guidelineToYAML(guidelineItems, {
            relatedAsps: parseMultilineInput(aspOptionsText),
            operationCompanies: parseMultilineInput(operationCompanyOptionsText),
            mediaNames: parseMultilineInput(mediaOptionsText),
            chunkSettings: {
                chunk_context_before: chunkContextBefore ?? DEFAULT_CHUNK_SETTINGS.chunk_context_before,
                chunk_context_after: chunkContextAfter ?? DEFAULT_CHUNK_SETTINGS.chunk_context_after,
                max_chunk_size: maxChunkSize ?? DEFAULT_CHUNK_SETTINGS.max_chunk_size,
                chunk_overlap: chunkOverlap ?? DEFAULT_CHUNK_SETTINGS.chunk_overlap,
                keyword_group_distance: keywordGroupDistance ?? DEFAULT_CHUNK_SETTINGS.keyword_group_distance,
            },
            keywordList: parseMultilineInput(keywordListText),
            enableSearch,
            searchKeywords: parseMultilineInput(searchKeywordsText),
            enableFiltering,
            titleExtractionKeywords: parseMultilineInput(titleExtractionKeywordsText),
            autoExpandKeywords: parseMultilineInput(autoExpandKeywordsText),
            enableUnfixableClassification,
            unfixableByDomain,
        });
    }, [
        guidelineItems,
        aspOptionsText,
        operationCompanyOptionsText,
        mediaOptionsText,
        chunkContextBefore,
        chunkContextAfter,
        maxChunkSize,
        chunkOverlap,
        keywordGroupDistance,
        keywordListText,
        enableSearch,
        searchKeywordsText,
        enableFiltering,
        titleExtractionKeywordsText,
        autoExpandKeywordsText,
        enableUnfixableClassification,
        unfixableByDomain,
    ]);

    const handleDownloadUnfixableRules = async () => {
        if (!selectedGuideline) return;
        try {
            setError(null);
            const { blob, filename } = await guidelinesApi.downloadUnfixableRulesExcel(
                selectedGuideline,
                currentVersion ?? undefined
            );
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download =
                filename ||
                buildUnfixableRulesExportFilename(
                    getGuidelineDisplayName(selectedGuideline),
                    currentVersion
                );
            document.body.appendChild(a);
            a.click();
            a.remove();
            URL.revokeObjectURL(url);
        } catch (err: any) {
            setError(`修正不可リストのダウンロードに失敗しました: ${err?.message || '不明なエラー'}`);
        }
    };

    // Toggle category collapse
    const toggleCategoryCollapse = (categoryId: string) => {
        setCollapsedCategories(prev => {
            const newSet = new Set(prev);
            if (newSet.has(categoryId)) {
                newSet.delete(categoryId);
            } else {
                newSet.add(categoryId);
            }
            return newSet;
        });
    };

    // Toggle rule collapse
    const toggleRuleCollapse = (ruleKey: string) => {
        setCollapsedRules(prev => {
            const newSet = new Set(prev);
            if (newSet.has(ruleKey)) {
                newSet.delete(ruleKey);
            } else {
                newSet.add(ruleKey);
            }
            return newSet;
        });
    };

    // Drag and drop sensors
    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    // Handle category reordering
    const handleCategoryDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            setGuidelineItems((items) => {
                const oldIndex = items.findIndex(item => item.id === active.id);
                const newIndex = items.findIndex(item => item.id === over.id);
                const newItems = arrayMove(items, oldIndex, newIndex);
                setHasChanges(true);
                return newItems;
            });
        }
    };

    // Load guidelines on mount
    useEffect(() => {
        loadGuidelines();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Load guideline when guideline is selected
    useEffect(() => {
        if (selectedGuideline) {
            loadVersions(selectedGuideline);
            loadGuideline(selectedGuideline);
        } else {
            setGuidelineItems([]);
            setEditingYamlContent('');
            setHasChanges(false);
            setVersions([]);
            setCurrentVersion(null);
            setAspOptionsText('');
            setOperationCompanyOptionsText('');
            setMediaOptionsText('');
            setChunkContextBefore(DEFAULT_CHUNK_SETTINGS.chunk_context_before);
            setChunkContextAfter(DEFAULT_CHUNK_SETTINGS.chunk_context_after);
            setMaxChunkSize(DEFAULT_CHUNK_SETTINGS.max_chunk_size);
            setChunkOverlap(DEFAULT_CHUNK_SETTINGS.chunk_overlap);
            setKeywordGroupDistance(DEFAULT_CHUNK_SETTINGS.keyword_group_distance);
            setKeywordListText('');
            setEnableSearch(false);
            setSearchKeywordsText('');
            setTitleExtractionKeywordsText('');
            setAutoExpandKeywordsText('');
            setPersistedForbiddenImagePreviews((prev) => {
                for (const url of Object.values(prev)) {
                    URL.revokeObjectURL(url);
                }
                return {};
            });
            setPersistedForbiddenImageLoadFailed({});
        }
        // Reset collapse state when switching guidelines
        setCollapsedCategories(new Set());
        setCollapsedRules(new Set());
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedGuideline]);

    // Sync editingYamlContent when switching to YAML mode or when guidelineItems change
    useEffect(() => {
        if (viewMode === 'yaml') {
            setEditingYamlContent(yamlContent);
        }
    }, [viewMode, yamlContent]);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (versionDropdownRef.current && !versionDropdownRef.current.contains(event.target as Node)) {
                setShowVersionDropdown(false);
                setVersionSearchTerm('');
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    // Filter versions based on search term and sort by creation date (newest first)
    const filteredVersions = useMemo(() => {
        let result = versions;

        // Filter if search term exists
        if (versionSearchTerm.trim()) {
            const searchLower = versionSearchTerm.toLowerCase();
            result = versions.filter(v => {
                const description = v.description || '説明なし';
                const date = new Date(v.created_at).toLocaleDateString('ja-JP');
                const versionText = `v${v.version} ${v.is_latest ? '最新' : ''} ${description} ${date}`;
                return versionText.toLowerCase().includes(searchLower);
            });
        }

        // Sort by creation date (newest first)
        return [...result].sort((a, b) => {
            const dateA = new Date(a.created_at).getTime();
            const dateB = new Date(b.created_at).getTime();
            return dateB - dateA; // Descending order (newest first)
        });
    }, [versions, versionSearchTerm]);

    const loadGuidelines = async () => {
        setLoading(true);
        try {
            const data = await guidelinesApi.getGuidelines() as any;
            const guidelinesList = (data?.guidelines || data || []) as Guideline[];
            setGuidelines(guidelinesList);
            if (guidelinesList.length > 0 && !selectedGuideline) {
                setSelectedGuideline(guidelinesList[0].id);
            }
        } catch (err: any) {
            console.error('Failed to load guidelines', err);
            setGuidelines([]);
            setError(`ガイドラインの読み込みに失敗しました: ${err?.message || '不明なエラー'}`);
        } finally {
            setLoading(false);
        }
    };

    const loadVersions = async (guidelineId: string) => {
        try {
            const data = await guidelinesApi.getGuidelineVersions(guidelineId) as any;
            const versionsList = (data?.versions || []) as GuidelineVersionInfo[];
            // Sort versions by creation date (newest first)
            const sortedVersions = [...versionsList].sort((a, b) => {
                const dateA = new Date(a.created_at).getTime();
                const dateB = new Date(b.created_at).getTime();
                return dateB - dateA; // Descending order (newest first)
            });
            setVersions(sortedVersions);
            // Set current version to latest
            const latest = sortedVersions.find(v => v.is_latest);
            if (latest) {
                setCurrentVersion(latest.version);
            } else if (sortedVersions.length > 0) {
                setCurrentVersion(sortedVersions[0].version);
            }
        } catch (err: any) {
            console.error('Failed to load versions', err);
            setVersions([]);
        }
    };

    const loadGuideline = async (guidelineId: string, version?: number) => {
        setLoading(true);
        setError(null);
        setPersistedForbiddenImagePreviews((prev) => {
            for (const url of Object.values(prev)) {
                URL.revokeObjectURL(url);
            }
            return {};
        });
        setPersistedForbiddenImageLoadFailed({});
        try {
            const data = await guidelinesApi.getGuideline(guidelineId, version) as GuidelineResponse;
            const items = (data?.items || []) as GuidelineItem[];

            let metadataAppliedFromYaml = false;
            let loadedItems: GuidelineItem[] = [];
            if (data?.yaml && (!items || items.length === 0)) {
                const parsed = parseGuidelineYaml(data.yaml);
                loadedItems = parsed.items;
                setGuidelineItems(loadedItems);
                if (parsed.metadata) {
                    applyMetadataFromYaml(parsed.metadata);
                    metadataAppliedFromYaml = true;
                }
            } else {
                loadedItems = items ?? [];
                setGuidelineItems(loadedItems);
            }
            // Collapse all categories by default
            setCollapsedCategories(new Set(loadedItems.map(item => item.id)));
            if (data?.version !== undefined) {
                setCurrentVersion(data.version);
            }
            if (!metadataAppliedFromYaml) {
                setAspOptionsText(listToMultiline(data?.aspOptions ?? []));
                setOperationCompanyOptionsText(listToMultiline(data?.operationCompanyOptions ?? []));
                setMediaOptionsText(listToMultiline(data?.mediaOptions ?? []));

                // For new guidelines: if no version exists OR all chunk settings are 0/undefined/null, use defaults
                // Otherwise, use the actual values (even if 0, as user may have explicitly set them)
                const allChunkSettingsAreZeroOrMissing =
                    (data?.chunkContextBefore === 0 || data?.chunkContextBefore === undefined || data?.chunkContextBefore === null) &&
                    (data?.chunkContextAfter === 0 || data?.chunkContextAfter === undefined || data?.chunkContextAfter === null) &&
                    (data?.maxChunkSize === 0 || data?.maxChunkSize === undefined || data?.maxChunkSize === null) &&
                    (data?.chunkOverlap === 0 || data?.chunkOverlap === undefined || data?.chunkOverlap === null) &&
                    (data?.keywordGroupDistance === 0 || data?.keywordGroupDistance === undefined || data?.keywordGroupDistance === null);

                const isNewGuideline = !data?.version || allChunkSettingsAreZeroOrMissing;

                setChunkContextBefore(
                    isNewGuideline
                        ? DEFAULT_CHUNK_SETTINGS.chunk_context_before
                        : (data?.chunkContextBefore ?? DEFAULT_CHUNK_SETTINGS.chunk_context_before)
                );
                setChunkContextAfter(
                    isNewGuideline
                        ? DEFAULT_CHUNK_SETTINGS.chunk_context_after
                        : (data?.chunkContextAfter ?? DEFAULT_CHUNK_SETTINGS.chunk_context_after)
                );
                setMaxChunkSize(
                    isNewGuideline
                        ? DEFAULT_CHUNK_SETTINGS.max_chunk_size
                        : (data?.maxChunkSize ?? DEFAULT_CHUNK_SETTINGS.max_chunk_size)
                );
                setChunkOverlap(
                    isNewGuideline
                        ? DEFAULT_CHUNK_SETTINGS.chunk_overlap
                        : (data?.chunkOverlap ?? DEFAULT_CHUNK_SETTINGS.chunk_overlap)
                );
                setKeywordGroupDistance(
                    isNewGuideline
                        ? DEFAULT_CHUNK_SETTINGS.keyword_group_distance
                        : (data?.keywordGroupDistance ?? DEFAULT_CHUNK_SETTINGS.keyword_group_distance)
                );
                setKeywordListText(listToMultiline(data?.keywordList ?? []));
                setEnableSearch(data?.enableSearch ?? false);
                setSearchKeywordsText(listToMultiline(data?.searchKeywords ?? []));
                setEnableFiltering(data?.enableFiltering ?? true);
                const titleExtraction = (data as any)?.titleExtractionKeywords ?? [];
                setTitleExtractionKeywordsText(listToMultiline(titleExtraction));
                setAutoExpandKeywordsText(listToMultiline((data as any)?.autoExpandKeywords ?? []));
                setEnableUnfixableClassification(data?.enableUnfixableClassification ?? false);
                const loadedUnfixable = (data as any)?.unfixableByDomain ?? [];
                setUnfixableByDomain(
                    sortUnfixableRulesBySourceRow(
                        Array.isArray(loadedUnfixable)
                            ? loadedUnfixable.map((r: any) => ({
                                  id: r.id || crypto.randomUUID(),
                                  domain: r.domain,
                                  description: r.description,
                                  asp: r.asp ?? '',
                                  operation_company:
                                      r.operation_company ?? r.operationCompany ?? '',
                                  media_name: r.media_name ?? r.mediaName ?? '',
                                  source_row: r.source_row ?? r.sourceRow ?? null,
                              }))
                            : []
                    )
                );
                setPendingUnfixableFile(null);
                setUnfixableUploadError(null);
            }
            setHasChanges(false);
            void prefetchPersistedForbiddenImagesForItems(loadedItems);
        } catch (err: any) {
            console.error('Failed to load guideline', err);
            setGuidelineItems([]);
            setAspOptionsText('');
            setOperationCompanyOptionsText('');
            setMediaOptionsText('');
            setChunkContextBefore(DEFAULT_CHUNK_SETTINGS.chunk_context_before);
            setChunkContextAfter(DEFAULT_CHUNK_SETTINGS.chunk_context_after);
            setMaxChunkSize(DEFAULT_CHUNK_SETTINGS.max_chunk_size);
            setChunkOverlap(DEFAULT_CHUNK_SETTINGS.chunk_overlap);
            setKeywordGroupDistance(DEFAULT_CHUNK_SETTINGS.keyword_group_distance);
            setKeywordListText('');
            setEnableSearch(false);
            setSearchKeywordsText('');
            setTitleExtractionKeywordsText('');
            setAutoExpandKeywordsText('');
            setEnableUnfixableClassification(false);
            setUnfixableByDomain([]);
            setPendingUnfixableFile(null);
            setUnfixableUploadError(null);
            setHasChanges(false);
            setError(`ガイドラインの読み込みに失敗しました: ${err?.message || '不明なエラー'}`);
        } finally {
            setLoading(false);
        }
    };

    const handleVersionChange = async (version: number) => {
        if (!selectedGuideline) return;
        setCurrentVersion(version);
        setShowVersionDropdown(false);
        setVersionSearchTerm('');
        await loadGuideline(selectedGuideline, version);
    };

    // Check if current version is the latest (editable)
    const isLatestVersion = useMemo(() => {
        if (currentVersion === null) return false;
        const versionInfo = versions.find(v => v.version === currentVersion);
        return versionInfo?.is_latest || false;
    }, [currentVersion, versions]);

    const handleSave = async () => {
        if (!selectedGuideline) return;

        try {
            setSaving(true);
            setError(null);
            setSuccess(null);

            // If in YAML mode, parse YAML back to items first
            let itemsToSave = guidelineItems;
            let metadataForPayload = {
                aspOptions: parseMultilineInput(aspOptionsText),
                operationCompanies: parseMultilineInput(operationCompanyOptionsText),
                mediaOptions: parseMultilineInput(mediaOptionsText),
                chunkContextBefore,
                chunkContextAfter,
                maxChunkSize,
                chunkOverlap,
                keywordGroupDistance,
                keywordList: parseMultilineInput(keywordListText),
                enableSearch,
                searchKeywords: parseMultilineInput(searchKeywordsText),
                enableFiltering,
                titleExtractionKeywords: parseMultilineInput(titleExtractionKeywordsText),
                autoExpandKeywords: parseMultilineInput(autoExpandKeywordsText),
                enableUnfixableClassification,
                unfixableByDomain,
            };

            if (viewMode === 'yaml') {
                try {
                    const parsed = parseGuidelineYaml(editingYamlContent);
                    itemsToSave = parsed.items;
                    setGuidelineItems(parsed.items);
                    void prefetchPersistedForbiddenImagesForItems(parsed.items);
                    applyMetadataFromYaml(parsed.metadata);

                    if (parsed.metadata) {
                        const chunkSettings = parsed.metadata.chunkSettings || {};
                        metadataForPayload = {
                            aspOptions: parsed.metadata.relatedAsps || [],
                            operationCompanies: parsed.metadata.operationCompanies || [],
                            mediaOptions: parsed.metadata.mediaNames || [],
                            chunkContextBefore: chunkSettings.chunk_context_before ?? DEFAULT_CHUNK_SETTINGS.chunk_context_before,
                            chunkContextAfter: chunkSettings.chunk_context_after ?? DEFAULT_CHUNK_SETTINGS.chunk_context_after,
                            maxChunkSize: chunkSettings.max_chunk_size ?? DEFAULT_CHUNK_SETTINGS.max_chunk_size,
                            chunkOverlap: chunkSettings.chunk_overlap ?? DEFAULT_CHUNK_SETTINGS.chunk_overlap,
                            keywordGroupDistance: chunkSettings.keyword_group_distance ?? DEFAULT_CHUNK_SETTINGS.keyword_group_distance,
                            keywordList: parsed.metadata.keywordList || [],
                            enableSearch: parsed.metadata.enableSearch ?? false,
                            searchKeywords: parsed.metadata.searchKeywords || [],
                            enableFiltering: parsed.metadata.enableFiltering ?? true,
                            titleExtractionKeywords: parsed.metadata.titleExtractionKeywords || [],
                            autoExpandKeywords: parsed.metadata.autoExpandKeywords || [],
                            enableUnfixableClassification:
                                parsed.metadata.enableUnfixableClassification ?? false,
                            unfixableByDomain: parsed.metadata.unfixableByDomain || [],
                        };
                        setEnableUnfixableClassification(
                            parsed.metadata.enableUnfixableClassification ?? false
                        );
                        setUnfixableByDomain(
                            sortUnfixableRulesBySourceRow(parsed.metadata.unfixableByDomain || [])
                        );
                    }
                } catch (parseErr: any) {
                    setError(`YAMLの解析に失敗しました: ${parseErr.message}`);
                    setSaving(false);
                    return;
                }
            }

            // If currently editing a category in form mode, include unsaved edits in the save payload.
            if (viewMode !== 'yaml' && editingItemId && editingItem) {
                const normalizedEditingItem = normalizeItemToMultipleRules(editingItem);
                itemsToSave = guidelineItems.map((it) => (it.id === editingItemId ? normalizedEditingItem : it));
            }

            // Save using items (single source of truth) - this creates a new version
            // PUT endpoint automatically creates a new version, even when editing an old version
            const isEditingOldVersion = !isLatestVersion;
            let description: string | undefined = undefined;

            if (isEditingOldVersion && currentVersion !== null) {
                description = prompt(`バージョン ${currentVersion} を編集しています。新しいバージョンを作成します。\n変更内容の説明を入力してください（任意）:`) || `バージョン ${currentVersion} から作成`;
            } else {
                description = prompt('変更内容の説明を入力してください（任意）:') || undefined;
            }

            // Use actual state values (even if 0), only fall back to defaults if undefined/null
            const getChunkValue = (value: number, defaultVal: number) => {
                return value !== undefined && value !== null ? Number(value) : defaultVal;
            };

            // Validate: if enable_search is true, search_keywords must not be empty
            if (metadataForPayload.enableSearch && metadataForPayload.searchKeywords.length === 0) {
                setError('検索を有効化する場合は、検索キーワードを入力してください。');
                setSaving(false);
                return;
            }

            const payload: UpdateGuidelineRequest = {
                items: itemsToSave,
                description,
                aspOptions: metadataForPayload.aspOptions,
                operationCompanyOptions: metadataForPayload.operationCompanies,
                mediaOptions: metadataForPayload.mediaOptions,
                chunkContextBefore: getChunkValue(metadataForPayload.chunkContextBefore, DEFAULT_CHUNK_SETTINGS.chunk_context_before),
                chunkContextAfter: getChunkValue(metadataForPayload.chunkContextAfter, DEFAULT_CHUNK_SETTINGS.chunk_context_after),
                maxChunkSize: getChunkValue(metadataForPayload.maxChunkSize, DEFAULT_CHUNK_SETTINGS.max_chunk_size),
                chunkOverlap: getChunkValue(metadataForPayload.chunkOverlap, DEFAULT_CHUNK_SETTINGS.chunk_overlap),
                keywordGroupDistance: getChunkValue(metadataForPayload.keywordGroupDistance, DEFAULT_CHUNK_SETTINGS.keyword_group_distance),
                keywordList: metadataForPayload.keywordList,
                enableSearch: metadataForPayload.enableSearch,
                searchKeywords: metadataForPayload.searchKeywords,
                enableFiltering: metadataForPayload.enableFiltering,
                titleExtractionKeywords: metadataForPayload.titleExtractionKeywords || [],
                autoExpandKeywords: metadataForPayload.autoExpandKeywords || [],
                enableUnfixableClassification: metadataForPayload.enableUnfixableClassification,
                unfixableByDomain: metadataForPayload.unfixableByDomain,
            };

            // Only send local files that are referenced by localref:// in the payload.
            const neededLocalIds = new Set<string>();
            for (const item of itemsToSave || []) {
                const rules = (item as any)?.rules || [];
                for (const rule of rules) {
                    const type = (rule?.type || '') as string;
                    const assets =
                        type === 'forbidden_image'
                            ? ((rule?.forbidden_image_assets || []) as any[])
                            : type === 'required_image_match'
                                ? ((rule?.required_image_assets || []) as any[])
                                : [];
                    for (const a of assets) {
                        const fp = String(a?.file_path || '');
                        if (fp.startsWith('localref://')) {
                            neededLocalIds.add(fp.replace('localref://', '').trim());
                        }
                    }
                }
            }

            const allLocalFiles = {
                ...(localForbiddenImageFilesRef.current || {}),
                ...(localRequiredImageFilesRef.current || {}),
            };
            const localFilesToSend: Record<string, File> = {};
            for (const localId of Array.from(neededLocalIds)) {
                const f = allLocalFiles[localId];
                if (!f) {
                    setError(`画像ファイルが見つかりません: localref://${localId}`);
                    setSaving(false);
                    return;
                }
                localFilesToSend[localId] = f;
            }

            await guidelinesApi.updateGuidelineWithAssets(
                selectedGuideline,
                payload,
                localFilesToSend,
                pendingUnfixableFile
            );
            setPendingUnfixableFile(null);

            // Reload versions to get the new version
            await loadVersions(selectedGuideline);
            const newVersions = await guidelinesApi.getGuidelineVersions(selectedGuideline) as any;
            const latest = (newVersions?.versions || []).find((v: GuidelineVersionInfo) => v.is_latest);
            if (latest) {
                setCurrentVersion(latest.version);
                await loadGuideline(selectedGuideline, latest.version);
            }

            setSuccess(isEditingOldVersion
                ? `バージョン ${currentVersion} を編集して新しいバージョンを作成しました`
                : 'ガイドラインを保存しました（新しいバージョンが作成されました）');
            setHasChanges(false);
            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            const detail =
                err?.response?.data?.detail ||
                err?.response?.detail ||
                err?.response?.data?.message ||
                err?.message ||
                '保存に失敗しました';
            setError(`保存に失敗しました: ${detail}`);
        } finally {
            setSaving(false);
        }
    };

    const handleAddItem = () => {
        const newItem: GuidelineItem = {
            id: `${NEW_CLIENT_CATEGORY_ID_PREFIX}${Date.now()}`,
            violation_category: '',
            rules: [], // Keep rules empty when adding new category
        };
        // Add to list and start editing
        setGuidelineItems(items => [...items, newItem]);
        setEditingItemId(newItem.id);
        setEditingItem(newItem);
        setHasChanges(true);
    };

    const ensureCategoryForbiddenImageDefaults = (item: GuidelineItem): GuidelineItem => {
        return {
            ...item,
        } as any;
    };

    const handleEditItem = (item: GuidelineItem) => {
        // Normalize to multiple rules format before editing
        const normalizedItem = ensureCategoryForbiddenImageDefaults(normalizeItemToMultipleRules(item));
        setEditingItemId(item.id);
        setEditingItem(normalizedItem);
    };

    const handleAddForbiddenImagesToRule = (ruleIndex: number, files: FileList | null) => {
        if (!files || !editingItem) return;
        const currentRules = editingItem.rules || [];
        if (!currentRules[ruleIndex]) return;

        const rule = { ...(currentRules[ruleIndex] as any) };
        const nextAssets = [...((rule.forbidden_image_assets as any[]) || [])];

        const nextLocalFiles: Record<string, File> = { ...localForbiddenImageFiles };
        const nextLocalPreviews: Record<string, string> = { ...localForbiddenImagePreviews };

        for (const file of Array.from(files)) {
            const localId = `local_${Date.now()}_${Math.random().toString(16).slice(2)}`;
            const previewUrl = URL.createObjectURL(file);
            nextLocalFiles[localId] = file;
            nextLocalPreviews[localId] = previewUrl;
            nextAssets.push({
                id: localId,
                file_path: `localref://${localId}`,
                content_type: file.type || 'image/png',
                original_filename: file.name,
            });
        }

        rule.forbidden_image_assets = nextAssets;
        if (typeof rule.similarity_threshold !== 'number') {
            rule.similarity_threshold = 0.97;
        }

        const newRules = [...currentRules];
        newRules[ruleIndex] = rule;
        setLocalForbiddenImageFiles(nextLocalFiles);
        localForbiddenImageFilesRef.current = nextLocalFiles;
        setLocalForbiddenImagePreviews(nextLocalPreviews);
        handleUpdateEditingItem({ ...(editingItem as any), rules: newRules } as any);
    };

    const handleAddRequiredImagesToRule = (ruleIndex: number, files: FileList | null) => {
        if (!files || !editingItem) return;
        const currentRules = editingItem.rules || [];
        if (!currentRules[ruleIndex]) return;

        const rule = { ...(currentRules[ruleIndex] as any) };
        const nextAssets = [...((rule.required_image_assets as any[]) || [])];

        const nextLocalFiles: Record<string, File> = { ...localRequiredImageFiles };
        const nextLocalPreviews: Record<string, string> = { ...localRequiredImagePreviews };

        for (const file of Array.from(files)) {
            const localId = `local_${Date.now()}_${Math.random().toString(16).slice(2)}`;
            const previewUrl = URL.createObjectURL(file);
            nextLocalFiles[localId] = file;
            nextLocalPreviews[localId] = previewUrl;
            nextAssets.push({
                id: localId,
                file_path: `localref://${localId}`,
                content_type: file.type || 'image/png',
                original_filename: file.name,
            });
        }

        rule.required_image_assets = nextAssets;
        const newRules = [...currentRules];
        newRules[ruleIndex] = rule;
        setLocalRequiredImageFiles(nextLocalFiles);
        localRequiredImageFilesRef.current = nextLocalFiles;
        setLocalRequiredImagePreviews(nextLocalPreviews);
        handleUpdateEditingItem({ ...(editingItem as any), rules: newRules } as any);
    };

    const handleRemoveForbiddenImageAssetFromRule = (ruleIndex: number, assetId: string) => {
        if (!editingItem) return;
        const currentRules = editingItem.rules || [];
        if (!currentRules[ruleIndex]) return;
        const rule = { ...(currentRules[ruleIndex] as any) };
        const assets = [...((rule.forbidden_image_assets as any[]) || [])];
        const asset = assets.find((a) => (a?.id || '') === assetId);
        const nextAssets = assets.filter((a) => (a?.id || '') !== assetId);

        if (asset?.file_path && String(asset.file_path).startsWith('localref://')) {
            const localId = String(asset.file_path).replace('localref://', '');
            const preview = localForbiddenImagePreviews[localId];
            if (preview) URL.revokeObjectURL(preview);
            const nextLocalFiles = { ...localForbiddenImageFiles };
            const nextLocalPreviews = { ...localForbiddenImagePreviews };
            delete nextLocalFiles[localId];
            delete nextLocalPreviews[localId];
            setLocalForbiddenImageFiles(nextLocalFiles);
            localForbiddenImageFilesRef.current = nextLocalFiles;
            setLocalForbiddenImagePreviews(nextLocalPreviews);
        }

        rule.forbidden_image_assets = nextAssets;
        const newRules = [...currentRules];
        newRules[ruleIndex] = rule;
        handleUpdateEditingItem({ ...(editingItem as any), rules: newRules } as any);
    };

    const handleRemoveRequiredImageAssetFromRule = (ruleIndex: number, assetId: string) => {
        if (!editingItem) return;
        const currentRules = editingItem.rules || [];
        if (!currentRules[ruleIndex]) return;
        const rule = { ...(currentRules[ruleIndex] as any) };
        const assets = [...((rule.required_image_assets as any[]) || [])];
        const asset = assets.find((a) => (a?.id || '') === assetId);
        const nextAssets = assets.filter((a) => (a?.id || '') !== assetId);

        if (asset?.file_path && String(asset.file_path).startsWith('localref://')) {
            const localId = String(asset.file_path).replace('localref://', '');
            const preview = localRequiredImagePreviews[localId];
            if (preview) URL.revokeObjectURL(preview);
            const nextLocalFiles = { ...localRequiredImageFiles };
            const nextLocalPreviews = { ...localRequiredImagePreviews };
            delete nextLocalFiles[localId];
            delete nextLocalPreviews[localId];
            setLocalRequiredImageFiles(nextLocalFiles);
            localRequiredImageFilesRef.current = nextLocalFiles;
            setLocalRequiredImagePreviews(nextLocalPreviews);
        }

        rule.required_image_assets = nextAssets;
        const newRules = [...currentRules];
        newRules[ruleIndex] = rule;
        handleUpdateEditingItem({ ...(editingItem as any), rules: newRules } as any);
    };

    const handlePreviewPersistedForbiddenImage = async (filePath: string) => {
        if (!filePath || filePath.startsWith('localref://')) return;
        let alreadyHas = false;
        setPersistedForbiddenImagePreviews((prev) => {
            alreadyHas = !!prev[filePath];
            return prev;
        });
        if (alreadyHas) return;
        try {
            const blob = await guidelinesApi.getGuidelineAssetBlob(filePath);
            const url = URL.createObjectURL(blob);
            setPersistedForbiddenImagePreviews((prev) => {
                if (prev[filePath]) {
                    URL.revokeObjectURL(url);
                    return prev;
                }
                return { ...prev, [filePath]: url };
            });
            setPersistedForbiddenImageLoadFailed((f) => {
                const n = { ...f };
                delete n[filePath];
                return n;
            });
        } catch (e) {
            setPersistedForbiddenImageLoadFailed((f) => ({ ...f, [filePath]: true }));
            setError('画像プレビューの取得に失敗しました');
        }
    };

    const handleCancelEdit = () => {
        // Only discard rows created via「カテゴリを追加」; persisted categories may use ids like temp_* (e.g. forbidden-image paths).
        if (editingItemId && editingItemId.startsWith(NEW_CLIENT_CATEGORY_ID_PREFIX)) {
            setGuidelineItems(items => items.filter(item => item.id !== editingItemId));
        }
        setEditingItemId(null);
        setEditingItem(null);
    };

    const handleDeleteItem = (itemId: string) => {
        if (window.confirm('この違反カテゴリを削除しますか？')) {
            setGuidelineItems(items => items.filter(item => item.id !== itemId));
            setHasChanges(true);
        }
    };

    // Helper function to normalize violation category: convert legacy single rule to multiple rules format
    // Each violation category can have multiple rules
    const normalizeItemToMultipleRules = (item: GuidelineItem): GuidelineItem & { rules: GuidelineRule[] } => {
        // If already has rules array, ensure each rule has type
        if (item.rules && item.rules.length > 0) {
            // Ensure all rules have type (migrate from item level if needed)
            const normalizedRules: GuidelineRule[] = item.rules.map(rule => ({
                ...rule,
                type: (rule.type || item.type || 'description') as string,
                patrol_source: rule.patrol_source || item.patrol_source,
            }));
            return {
                ...item,
                rules: normalizedRules,
            };
        }

        // Convert legacy single rule to rules array format
        const hasLegacyFields = item.violation_check_prompt ||
            item.forbidden_regular_expressions ||
            item.musthave_regular_expressions ||
            item.detail;

        if (hasLegacyFields) {
            const rule: GuidelineRule = {
                id: `rule_${Date.now()}`,
                type: item.type || 'description', // Type moved to rule level
                patrol_source: item.patrol_source,
                violation_check_prompt: item.violation_check_prompt,
                existence_check_prompt: item.existence_check_prompt,
                forbidden_regular_expressions: item.forbidden_regular_expressions,
                forbidden_detect_message: item.forbidden_detect_message,
                musthave_regular_expressions: item.musthave_regular_expressions,
                musthave_not_detect_message: item.musthave_not_detect_message,
                regular_expression_conditions: item.regular_expression_conditions,
                context_window: item.context_window,
                detail: item.detail,
            };
            return {
                ...item,
                rules: [rule],
                // Keep legacy fields for backward compatibility
            };
        }

        // If no rules at all, initialize with empty rules array
        return {
            ...item,
            rules: [] as GuidelineRule[],
        };
    };

    const handleSaveItem = (itemId: string) => {
        if (!editingItem) return;

        // Validate required fields
        if (!editingItem.id || !editingItem.violation_category) {
            setError('IDとカテゴリ名は必須です');
            return;
        }

        // Normalize to multiple rules format
        const normalizedItem = normalizeItemToMultipleRules(editingItem);

        // Update the item in the list
        setGuidelineItems(items =>
            items.map(item => item.id === itemId ? normalizedItem : item)
        );

        setEditingItemId(null);
        setEditingItem(null);
        setHasChanges(true);
        setError(null);
    };

    const handleUpdateEditingItem = (updatedItem: GuidelineItem) => {
        setEditingItem(updatedItem);
        setHasChanges(true);
    };

    const handleAddGuideline = () => {
        setNewGuideline({ name: '' });
        setShowAddGuidelineModal(true);
    };

    const handleSaveGuideline = async () => {
        if (!newGuideline.name) {
            setError('名前を入力してください');
            return;
        }

        try {
            setLoading(true);
            setError(null);
            const createdGuideline = await guidelinesApi.createGuideline({ name: newGuideline.name } as Omit<Guideline, 'versions'>);
            await loadGuidelines();
            setShowAddGuidelineModal(false);
            setNewGuideline({ name: '' });
            setSuccess('ガイドラインを追加しました');
            setTimeout(() => setSuccess(null), 3000);
            // Auto-select the new guideline
            setSelectedGuideline(createdGuideline.id);
        } catch (err: any) {
            setError(err.response?.data?.detail || 'ガイドラインの追加に失敗しました');
            setLoading(false);
            setTimeout(() => setSuccess(null), 5000);
            // Uncomment when backend is ready:
            // setError(`クライアントの追加に失敗しました: ${err.message}`);
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteGuideline = async (guidelineId: string) => {
        if (!window.confirm(`ガイドライン「${getGuidelineDisplayName(guidelineId)}」を削除しますか？\nこの操作は取り消せません。`)) {
            return;
        }

        try {
            setDeletingGuidelineId(guidelineId);
            setError(null);
            await guidelinesApi.deleteGuideline(guidelineId);
            await loadGuidelines();

            // If deleted guideline was selected, select another one or clear selection
            if (selectedGuideline === guidelineId) {
                const remainingGuidelines = guidelines.filter(g => g.id !== guidelineId);
                if (remainingGuidelines.length > 0) {
                    setSelectedGuideline(remainingGuidelines[0].id);
                } else {
                    setSelectedGuideline(null);
                    setGuidelineItems([]);
                    setEditingYamlContent('');
                }
            }

            setSuccess('ガイドラインを削除しました');
            setTimeout(() => setSuccess(null), 3000);
        } catch (err: any) {
            // For now, remove from local state until backend is implemented
            setGuidelines(prev => prev.filter(g => g.id !== guidelineId));

            // If deleted guideline was selected, select another one or clear selection
            if (selectedGuideline === guidelineId) {
                const remainingGuidelines = guidelines.filter(g => g.id !== guidelineId);
                if (remainingGuidelines.length > 0) {
                    setSelectedGuideline(remainingGuidelines[0].id);
                } else {
                    setSelectedGuideline(null);
                    setGuidelineItems([]);
                    setEditingYamlContent('');
                }
            }

            setSuccess('ガイドラインを削除しました');
            setTimeout(() => setSuccess(null), 5000);
            // Uncomment when backend is ready:
            // setError(`クライアントの削除に失敗しました: ${err.message}`);
        } finally {
            setDeletingGuidelineId(null);
        }
    };

    const handleYamlChange = (value: string) => {
        setEditingYamlContent(value);
        setHasChanges(true);
        // Try to parse YAML and update items in real-time (optional, for validation)
        try {
            parseGuidelineYaml(value);
            setError(null);
        } catch (parseErr: any) {
            // Show validation error immediately (does not block typing)
            setError(`YAMLの解析に失敗しました: ${parseErr.message}`);
        }
    };

    // Parse YAML back to guideline items when switching from YAML to form mode
    const handleViewModeChange = (mode: 'yaml' | 'form') => {
        if (viewMode === 'yaml' && mode === 'form') {
            // Switching from YAML to form - parse YAML and update items + metadata
            try {
                const parsed = parseGuidelineYaml(editingYamlContent);
                setGuidelineItems(parsed.items);
                void prefetchPersistedForbiddenImagesForItems(parsed.items);
                // Collapse all categories by default when switching to form mode
                setCollapsedCategories(new Set(parsed.items.map(item => item.id)));
                applyMetadataFromYaml(parsed.metadata);
                setError(null);
            } catch (parseErr: any) {
                setError(`YAMLの解析に失敗しました: ${parseErr.message}`);
                // Don't switch mode if YAML is invalid
                return;
            }
        }
        setViewMode(mode);
    };

    const getGuidelineDisplayName = (guidelineId: string) => {
        const guideline = guidelines.find(g => g.id === guidelineId);
        return guideline?.name || guidelineId;
    };

    const getTypeDisplayName = (type: string) => {
        const typeMap: Record<string, string> = {
            'description': '説明',
            'text_check': 'テキストチェック',
            'forbidden_regular_expression_check': '禁止表現チェック',
            'musthave_regular_expression_check': '必須表現チェック',
            'forbidden_image': '禁止画像チェック',
            'ad_existence': '広告存在チェック',
            'link': 'リンクチェック',
            'asp': 'ASPチェック',
            'image_check': '画像チェック',
            'unknown': '未定義',
        };
        return typeMap[type] || type;
    };

    // Filter guidelines based on search query
    const filteredGuidelines = useMemo(() => {
        if (!searchQuery.trim()) {
            return guidelines;
        }
        const query = searchQuery.toLowerCase().trim();
        return guidelines.filter(guideline =>
            guideline.id.toLowerCase().includes(query) ||
            guideline.name.toLowerCase().includes(query)
        );
    }, [guidelines, searchQuery]);

    return (
        <div className="page-container">
            <div className="page-header">
                <h1 className="page-title">ガイドライン管理</h1>
                <p className="page-description">
                    各ガイドラインを管理・編集します
                </p>
            </div>

            {error && (
                <div className="alert alert-error">
                    <AlertCircle size={20} />
                    <span>{error}</span>
                    <button onClick={() => setError(null)} className="alert-close">
                        <X size={16} />
                    </button>
                </div>
            )}

            {success && (
                <div className="alert alert-success">
                    <CheckCircle2 size={20} />
                    <span>{success}</span>
                </div>
            )}

            <div className="guideline-container">
                {/* Guideline Sidebar */}
                <div className="guideline-sidebar">
                    <div className="sidebar-header">
                        <h3>ガイドライン</h3>
                        <button
                            className="btn-icon btn-icon-add"
                            onClick={handleAddGuideline}
                            title="ガイドラインを追加"
                        >
                            <Plus size={18} />
                        </button>
                    </div>
                    <div className="sidebar-search">
                        <div className="search-input-wrapper">
                            <Search size={16} className="search-icon" />
                            <input
                                type="text"
                                className="search-input"
                                placeholder="ガイドラインを検索..."
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                            />
                            {searchQuery && (
                                <button
                                    className="search-clear"
                                    onClick={() => setSearchQuery('')}
                                    title="検索をクリア"
                                >
                                    <X size={14} />
                                </button>
                            )}
                        </div>
                        {searchQuery && (
                            <div className="search-results-info">
                                {filteredGuidelines.length}件 / {guidelines.length}件
                            </div>
                        )}
                    </div>
                    <div className="guideline-list">
                        {loading && !guidelines.length ? (
                            <div className="loading-state">
                                <Loader2 className="spinner" size={20} />
                                <span>読み込み中...</span>
                            </div>
                        ) : guidelines.length === 0 ? (
                            <div className="empty-guidelines">
                                <p>ガイドラインがありません</p>
                                <button
                                    className="btn btn-primary btn-sm"
                                    onClick={handleAddGuideline}
                                >
                                    <Plus size={14} />
                                    ガイドラインを追加
                                </button>
                            </div>
                        ) : filteredGuidelines.length === 0 ? (
                            <div className="empty-guidelines">
                                <p>検索結果が見つかりません</p>
                                <button
                                    className="btn btn-secondary btn-sm"
                                    onClick={() => setSearchQuery('')}
                                >
                                    検索をクリア
                                </button>
                            </div>
                        ) : (
                            filteredGuidelines.map(guideline => (
                                <div
                                    key={guideline.id}
                                    className={`guideline-item-wrapper ${selectedGuideline === guideline.id ? 'active' : ''}`}
                                >
                                    <button
                                        className={`guideline-item ${selectedGuideline === guideline.id ? 'active' : ''}`}
                                        onClick={() => setSelectedGuideline(guideline.id)}
                                    >
                                        <FileText size={18} />
                                        <span>{guideline.name}</span>
                                        {selectedGuideline === guideline.id && (
                                            <ChevronRight size={16} className="active-indicator" />
                                        )}
                                    </button>
                                    <button
                                        className="btn-icon btn-icon-danger btn-icon-sm"
                                        onClick={(e) => {
                                            e.stopPropagation();
                                            handleDeleteGuideline(guideline.id);
                                        }}
                                        disabled={deletingGuidelineId === guideline.id}
                                        title="削除"
                                    >
                                        {deletingGuidelineId === guideline.id ? (
                                            <Loader2 className="spinner" size={14} />
                                        ) : (
                                            <Trash2 size={14} />
                                        )}
                                    </button>
                                </div>
                            ))
                        )}
                    </div>
                </div>

                {/* Main Content */}
                <div className="guideline-main">
                    {!selectedGuideline ? (
                        <div className="empty-state">
                            <FileText size={48} />
                            <h3>ガイドラインを選択してください</h3>
                            <p>左側のリストからガイドラインを選択して、ガイドラインを編集します</p>
                        </div>
                    ) : (
                        <>
                            <div className="guideline-header">
                                <div className="header-left">
                                    <div className="title-row">
                                        <h2 className="guideline-title">
                                            {getGuidelineDisplayName(selectedGuideline)} のガイドライン
                                        </h2>
                                        {hasChanges && (
                                            <span className="unsaved-badge">未保存の変更</span>
                                        )}
                                    </div>
                                    {currentVersion !== null && (
                                        <div className="version-selector">
                                            <label>バージョン:</label>
                                            <div className="dropdown-container" ref={versionDropdownRef}>
                                                <button
                                                    className={`dropdown-button version-dropdown-button ${currentVersion ? 'selected' : ''}`}
                                                    onClick={() => {
                                                        if (!hasChanges) {
                                                            setShowVersionDropdown(!showVersionDropdown);
                                                            if (!showVersionDropdown) {
                                                                setVersionSearchTerm('');
                                                            }
                                                        }
                                                    }}
                                                    disabled={hasChanges}
                                                    title={hasChanges ? '未保存の変更があるため、バージョンを変更できません' : ''}
                                                >
                                                    <span>
                                                        {(() => {
                                                            const selectedVersion = versions.find(v => v.version === currentVersion);
                                                            if (selectedVersion) {
                                                                return `v${selectedVersion.version} ${selectedVersion.is_latest ? '(最新)' : ''} - ${selectedVersion.description || '説明なし'} (${new Date(selectedVersion.created_at).toLocaleDateString('ja-JP')})`;
                                                            }
                                                            return 'バージョンを選択';
                                                        })()}
                                                    </span>
                                                    <ChevronDown size={16} />
                                                </button>
                                                {showVersionDropdown && !hasChanges && (
                                                    <div className="dropdown-menu version-dropdown-menu">
                                                        <div className="dropdown-search">
                                                            <Search size={16} className="search-icon" />
                                                            <input
                                                                type="text"
                                                                className="search-input"
                                                                placeholder="バージョンを検索..."
                                                                value={versionSearchTerm}
                                                                onChange={(e) => setVersionSearchTerm(e.target.value)}
                                                                onClick={(e) => e.stopPropagation()}
                                                            />
                                                        </div>
                                                        <div className="dropdown-list">
                                                            {filteredVersions.length > 0 ? (
                                                                filteredVersions.map((v) => (
                                                                    <button
                                                                        key={v.version}
                                                                        className={`dropdown-item ${v.version === currentVersion ? 'active' : ''}`}
                                                                        onClick={() => handleVersionChange(v.version)}
                                                                    >
                                                                        <div className="version-item-content">
                                                                            <div className="version-item-header">
                                                                                <span className="version-number">v{v.version}</span>
                                                                                {v.is_latest && (
                                                                                    <span className="version-badge">最新</span>
                                                                                )}
                                                                            </div>
                                                                            <div className="version-item-description">
                                                                                {v.description || '説明なし'}
                                                                            </div>
                                                                            <div className="version-item-date">
                                                                                {new Date(v.created_at).toLocaleDateString('ja-JP')}
                                                                            </div>
                                                                        </div>
                                                                        {v.version === currentVersion && (
                                                                            <CheckCircle2 size={16} className="version-check-icon" />
                                                                        )}
                                                                    </button>
                                                                ))
                                                            ) : (
                                                                <div className="dropdown-no-results">
                                                                    検索結果が見つかりません
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}
                                            </div>
                                            {!versions.find(v => v.version === currentVersion)?.is_latest && hasChanges && (
                                                <span className="edit-notice">編集内容は新しいバージョンとして保存されます</span>
                                            )}
                                        </div>
                                    )}
                                </div>
                                <div className="header-actions">
                                    <div className="view-mode-toggle">
                                        <button
                                            className={`mode-btn ${viewMode === 'form' ? 'active' : ''}`}
                                            onClick={() => handleViewModeChange('form')}
                                        >
                                            フォーム
                                        </button>
                                        <button
                                            className={`mode-btn ${viewMode === 'yaml' ? 'active' : ''}`}
                                            onClick={() => handleViewModeChange('yaml')}
                                        >
                                            YAML
                                        </button>
                                    </div>
                                    <button
                                        className="btn btn-primary"
                                        onClick={handleSave}
                                        disabled={saving || !hasChanges}
                                        title={!isLatestVersion ? '編集内容は新しいバージョンとして保存されます' : ''}
                                    >
                                        {saving ? (
                                            <>
                                                <Loader2 className="spinner" size={16} />
                                                保存中...
                                            </>
                                        ) : (
                                            <>
                                                <Save size={16} />
                                                保存
                                            </>
                                        )}
                                    </button>
                                </div>
                            </div>

                            {loading ? (
                                <div className="loading-state">
                                    <Loader2 className="spinner" size={24} />
                                    <p>ガイドラインを読み込み中...</p>
                                </div>
                            ) : viewMode === 'yaml' ? (
                                <div className="yaml-editor-container">
                                    <div className="editor-header">
                                        <span className="editor-label">YAML形式で編集</span>
                                        {!isLatestVersion && hasChanges && (
                                            <span className="edit-notice">編集内容は新しいバージョンとして保存されます</span>
                                        )}
                                    </div>
                                    <div className="yaml-editor">
                                        <CodeMirror
                                            value={editingYamlContent}
                                            height="520px"
                                            basicSetup={{
                                                lineNumbers: true,
                                                foldGutter: true,
                                                highlightActiveLine: true,
                                            }}
                                            extensions={[
                                                yamlLanguage(),
                                                linter((view) => yamlDiagnostics(view.state.doc.toString())),
                                            ]}
                                            onChange={(value) => handleYamlChange(value)}
                                        />
                                    </div>
                                </div>
                            ) : (
                                <div className="guideline-content guideline-editor-body">
                                    {!isLatestVersion && hasChanges && (
                                        <div className="edit-notice">
                                            <AlertCircle size={20} />
                                            <p>このバージョンを編集しています。保存すると新しいバージョンが作成され、このバージョンは変更されません。</p>
                                        </div>
                                    )}
                                    <div className="organization-section">
                                        <h3 className="section-subtitle">関連組織リスト</h3>
                                        <div className="option-grid">
                                            <div className="option-card">
                                                <label className="option-label">関連ASP</label>
                                                <textarea
                                                    className="option-textarea"
                                                    placeholder="一行に1つのASPを入力"
                                                    value={aspOptionsText}
                                                    onChange={(e) => {
                                                        setAspOptionsText(e.target.value);
                                                        setHasChanges(true);
                                                    }}
                                                />
                                            </div>
                                            <div className="option-card">
                                                <label className="option-label">運営会社</label>
                                                <textarea
                                                    className="option-textarea"
                                                    placeholder="一行に1つの運営会社を入力"
                                                    value={operationCompanyOptionsText}
                                                    onChange={(e) => {
                                                        setOperationCompanyOptionsText(e.target.value);
                                                        setHasChanges(true);
                                                    }}
                                                />
                                            </div>
                                            <div className="option-card">
                                                <label className="option-label">媒体名</label>
                                                <textarea
                                                    className="option-textarea"
                                                    placeholder="一行に1つの媒体名を入力"
                                                    value={mediaOptionsText}
                                                    onChange={(e) => {
                                                        setMediaOptionsText(e.target.value);
                                                        setHasChanges(true);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                    </div>
                                    <div className="chunk-settings-section">
                                        <h3 className="section-subtitle">ページチャンク設定</h3>
                                        <div className="chunk-settings-grid">
                                            <div className="chunk-input">
                                                <label>CHUNK_CONTEXT_BEFORE</label>
                                                <input
                                                    type="number"
                                                    value={chunkContextBefore}
                                                    onChange={(e) => {
                                                        setChunkContextBefore(Number(e.target.value) || 0);
                                                        setHasChanges(true);
                                                    }}
                                                />
                                            </div>
                                            <div className="chunk-input">
                                                <label>CHUNK_CONTEXT_AFTER</label>
                                                <input
                                                    type="number"
                                                    value={chunkContextAfter}
                                                    onChange={(e) => {
                                                        setChunkContextAfter(Number(e.target.value) || 0);
                                                        setHasChanges(true);
                                                    }}
                                                />
                                            </div>
                                            <div className="chunk-input">
                                                <label>MAX_CHUNK_SIZE</label>
                                                <input
                                                    type="number"
                                                    value={maxChunkSize}
                                                    onChange={(e) => {
                                                        setMaxChunkSize(Number(e.target.value) || 0);
                                                        setHasChanges(true);
                                                    }}
                                                />
                                            </div>
                                            <div className="chunk-input">
                                                <label>CHUNK_OVERLAP</label>
                                                <input
                                                    type="number"
                                                    value={chunkOverlap}
                                                    onChange={(e) => {
                                                        setChunkOverlap(Number(e.target.value) || 0);
                                                        setHasChanges(true);
                                                    }}
                                                />
                                            </div>
                                            <div className="chunk-input">
                                                <label>KEYWORD_GROUP_DISTANCE</label>
                                                <input
                                                    type="number"
                                                    value={keywordGroupDistance}
                                                    onChange={(e) => {
                                                        setKeywordGroupDistance(Number(e.target.value) || 0);
                                                        setHasChanges(true);
                                                    }}
                                                />
                                            </div>
                                        </div>
                                        <div className="option-card keyword-card">
                                            <label className="option-label">キーワードリスト</label>
                                            <textarea
                                                className="option-textarea"
                                                placeholder="一行に1つのキーワードを入力"
                                                value={keywordListText}
                                                onChange={(e) => {
                                                    setKeywordListText(e.target.value);
                                                    setHasChanges(true);
                                                }}
                                            />
                                        </div>
                                    </div>
                                    <div className="category-section">
                                        <div className="category-header">
                                            <h3 className="section-subtitle">違反カテゴリ</h3>
                                            <button
                                                className="btn btn-secondary"
                                                onClick={handleAddItem}
                                            >
                                                <Plus size={16} />
                                                カテゴリを追加
                                            </button>
                                        </div>

                                        <DndContext
                                            sensors={sensors}
                                            collisionDetection={closestCenter}
                                            onDragEnd={handleCategoryDragEnd}
                                        >
                                            <SortableContext
                                                items={guidelineItems.map(item => item.id)}
                                                strategy={verticalListSortingStrategy}
                                            >
                                                <div className="guideline-items">
                                                    {guidelineItems.length === 0 ? (
                                                        <div className="empty-items">
                                                            <p>違反カテゴリがありません</p>
                                                            <button
                                                                className="btn btn-primary"
                                                                onClick={handleAddItem}
                                                            >
                                                                <Plus size={16} />
                                                                最初のカテゴリを追加
                                                            </button>
                                                        </div>
                                                    ) : (
                                                        guidelineItems.map((item, index) => {
                                                            const isEditing = editingItemId === item.id;
                                                            // Use editingItem when editing, otherwise use item
                                                            const itemToNormalize = isEditing && editingItem ? editingItem : item;
                                                            const normalizedItem = normalizeItemToMultipleRules(itemToNormalize);
                                                            const rulesCount = normalizedItem.rules?.length || 0;

                                                            return (
                                                                <SortableCategory
                                                                    key={item.id}
                                                                    item={item}
                                                                    index={index}
                                                                    isEditing={isEditing}
                                                                    editingItem={isEditing ? editingItem : null}
                                                                    normalizedItem={normalizedItem}
                                                                    rulesCount={rulesCount}
                                                                    isCollapsed={collapsedCategories.has(item.id)}
                                                                    onEdit={handleEditItem}
                                                                    onDelete={handleDeleteItem}
                                                                    onSave={handleSaveItem}
                                                                    onCancelEdit={handleCancelEdit}
                                                                    onUpdateEditingItem={handleUpdateEditingItem}
                                                                    onRulesReorder={(itemId, newRules) => {
                                                                        const updatedItem = { ...item, rules: newRules };
                                                                        setGuidelineItems((items: GuidelineItem[]) =>
                                                                            items.map((cat: GuidelineItem) => cat.id === itemId ? updatedItem : cat)
                                                                        );
                                                                        setHasChanges(true);
                                                                    }}
                                                                    onToggleCollapse={toggleCategoryCollapse}
                                                                    collapsedRules={collapsedRules}
                                                                    onToggleRuleCollapse={toggleRuleCollapse}
                                                                    getTypeDisplayName={getTypeDisplayName}
                                                                    localForbiddenImagePreviews={localForbiddenImagePreviews}
                                                                    localRequiredImagePreviews={localRequiredImagePreviews}
                                                                    persistedForbiddenImagePreviews={persistedForbiddenImagePreviews}
                                                                    persistedForbiddenImageLoadFailed={persistedForbiddenImageLoadFailed}
                                                                    onPreviewPersistedForbiddenImage={handlePreviewPersistedForbiddenImage}
                                                                    onAddForbiddenImagesToRule={handleAddForbiddenImagesToRule}
                                                                    onRemoveForbiddenImageAssetFromRule={handleRemoveForbiddenImageAssetFromRule}
                                                                    onAddRequiredImagesToRule={handleAddRequiredImagesToRule}
                                                                    onRemoveRequiredImageAssetFromRule={handleRemoveRequiredImageAssetFromRule}
                                                                />
                                                            );
                                                        })
                                                    )}
                                                </div>
                                            </SortableContext>
                                        </DndContext>
                                    </div>
                                    <div className="category-section">
                                        <div className="category-header">
                                            <h3 className="section-subtitle">検索設定</h3>
                                        </div>
                                        <div className="search-settings-content">
                                            <div className="search-toggle-section">
                                                <label className="search-toggle-label">
                                                    <input
                                                        type="checkbox"
                                                        checked={enableSearch}
                                                        onChange={(e) => {
                                                            setEnableSearch(e.target.checked);
                                                            setHasChanges(true);
                                                        }}
                                                    />
                                                    <span>検索を有効化</span>
                                                </label>
                                            </div>
                                            {enableSearch && (
                                                <div className="option-card search-keywords-card">
                                                    <label className="option-label">検索キーワード</label>
                                                    <textarea
                                                        className="option-textarea"
                                                        placeholder="一行に1つの検索キーワードを入力"
                                                        value={searchKeywordsText}
                                                        onChange={(e) => {
                                                            setSearchKeywordsText(e.target.value);
                                                            setHasChanges(true);
                                                        }}
                                                    />
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="category-section">
                                        <div className="category-header">
                                            <h3 className="section-subtitle">違反フィルター設定</h3>
                                        </div>
                                        <div className="search-settings-content">
                                            <div className="search-toggle-section">
                                                <label className="search-toggle-label">
                                                    <input
                                                        type="checkbox"
                                                        checked={enableFiltering}
                                                        onChange={(e) => {
                                                            setEnableFiltering(e.target.checked);
                                                            setHasChanges(true);
                                                        }}
                                                    />
                                                    <span>フィルタリングを有効化</span>
                                                </label>
                                            </div>
                                            {enableFiltering && (
                                                <div style={{ marginLeft: '20px', marginTop: '10px' }}>
                                                    <div className="option-card search-keywords-card" style={{ marginTop: '8px', marginBottom: '8px' }}>
                                                        <label className="option-label">その他コンテンツのキーワード</label>
                                                            <textarea
                                                                className="option-textarea"
                                                                placeholder="一行に1つのキーワードを入力"
                                                                value={titleExtractionKeywordsText}
                                                                onChange={(e) => {
                                                                    setTitleExtractionKeywordsText(e.target.value);
                                                                    setHasChanges(true);
                                                                }}
                                                            />
                                                    </div>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                    <div className="category-section">
                                        <div className="category-header">
                                            <h3 className="section-subtitle">自動展開設定</h3>
                                        </div>
                                        <div className="search-settings-content">
                                            <div style={{ marginLeft: '20px', marginTop: '10px' }}>
                                                <div className="option-card search-keywords-card" style={{ marginTop: '8px', marginBottom: '8px' }}>
                                                    <label className="option-label">自動展開クリック条件キーワード</label>
                                                    <textarea
                                                        className="option-textarea"
                                                        placeholder="一行に1つのキーワードを入力（ページ本文に含まれる場合のみ「もっと見る」等を自動クリック）"
                                                        value={autoExpandKeywordsText}
                                                        onChange={(e) => {
                                                            setAutoExpandKeywordsText(e.target.value);
                                                            setHasChanges(true);
                                                        }}
                                                    />
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                    <UnfixableRulesSection
                                        enableUnfixableClassification={enableUnfixableClassification}
                                        onEnableUnfixableClassificationChange={setEnableUnfixableClassification}
                                        unfixableByDomain={unfixableByDomain}
                                        onUnfixableByDomainChange={setUnfixableByDomain}
                                        unfixableUploadError={unfixableUploadError}
                                        onUnfixableUploadErrorChange={setUnfixableUploadError}
                                        pendingUnfixableFile={pendingUnfixableFile}
                                        onPendingUnfixableFileChange={setPendingUnfixableFile}
                                        onDownload={handleDownloadUnfixableRules}
                                        downloadDisabled={!selectedGuideline || unfixableByDomain.length === 0}
                                        onDirty={() => setHasChanges(true)}
                                    />
                                </div>
                            )}

                        </>
                    )}
                </div>
            </div>
            {/* Add Guideline Modal */}
            {showAddGuidelineModal && (
                <div className="modal-overlay" onClick={() => setShowAddGuidelineModal(false)}>
                    <div className="modal-content" onClick={(e) => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>ガイドラインを追加</h3>
                            <button className="btn-icon" onClick={() => setShowAddGuidelineModal(false)}>
                                <X size={20} />
                            </button>
                        </div>
                        <div className="modal-body">
                            <div className="item-form">
                                <div className="form-group">
                                    <label>名前 <span className="required">*</span></label>
                                    <input
                                        type="text"
                                        className="form-input"
                                        value={newGuideline.name || ''}
                                        onChange={(e) => setNewGuideline({ ...newGuideline, name: e.target.value })}
                                        placeholder="例: ニコス"
                                    />
                                    <p className="form-help">ガイドラインの名前を入力してください。IDは自動生成されます。</p>
                                </div>
                            </div>
                        </div>
                        <div className="modal-footer">
                            <button
                                className="btn btn-secondary"
                                onClick={() => setShowAddGuidelineModal(false)}
                            >
                                キャンセル
                            </button>
                            <button
                                className="btn btn-primary"
                                onClick={handleSaveGuideline}
                                disabled={loading}
                            >
                                {loading ? (
                                    <>
                                        <Loader2 className="spinner" size={16} />
                                        追加中...
                                    </>
                                ) : (
                                    <>
                                        <Plus size={16} />
                                        追加
                                    </>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

// Sortable Category Component
interface SortableCategoryProps {
    item: GuidelineItem;
    index: number;
    isEditing: boolean;
    editingItem: GuidelineItem | null;
    normalizedItem: GuidelineItem & { rules: GuidelineRule[] };
    rulesCount: number;
    isCollapsed: boolean;
    onEdit: (item: GuidelineItem) => void;
    onDelete: (itemId: string) => void;
    onSave: (itemId: string) => void;
    onCancelEdit: () => void;
    onUpdateEditingItem: (item: GuidelineItem) => void;
    onRulesReorder: (itemId: string, newRules: GuidelineRule[]) => void;
    onToggleCollapse: (categoryId: string) => void;
    collapsedRules: Set<string>;
    onToggleRuleCollapse: (ruleKey: string) => void;
    getTypeDisplayName: (type: string) => string;
    localForbiddenImagePreviews: Record<string, string>;
    localRequiredImagePreviews: Record<string, string>;
    persistedForbiddenImagePreviews: Record<string, string>;
    persistedForbiddenImageLoadFailed: Record<string, boolean>;
    onPreviewPersistedForbiddenImage: (filePath: string) => void;
    onAddForbiddenImagesToRule: (ruleIndex: number, files: FileList | null) => void;
    onRemoveForbiddenImageAssetFromRule: (ruleIndex: number, assetId: string) => void;
    onAddRequiredImagesToRule: (ruleIndex: number, files: FileList | null) => void;
    onRemoveRequiredImageAssetFromRule: (ruleIndex: number, assetId: string) => void;
}

const SortableCategory: React.FC<SortableCategoryProps> = ({
    item,
    index,
    isEditing,
    editingItem,
    normalizedItem,
    rulesCount,
    isCollapsed,
    onEdit,
    onDelete,
    onSave,
    onCancelEdit,
    onUpdateEditingItem,
    onRulesReorder,
    onToggleCollapse,
    collapsedRules,
    onToggleRuleCollapse,
    getTypeDisplayName,
    localForbiddenImagePreviews,
    localRequiredImagePreviews,
    persistedForbiddenImagePreviews,
    persistedForbiddenImageLoadFailed,
    onPreviewPersistedForbiddenImage,
    onAddForbiddenImagesToRule,
    onRemoveForbiddenImageAssetFromRule,
    onAddRequiredImagesToRule,
    onRemoveRequiredImageAssetFromRule,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: item.id });

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1
    };

    return (
        <div
            ref={setNodeRef}
            style={style}
            className={`guideline-item-card ${isEditing ? 'editing' : ''} ${isDragging ? 'dragging' : ''}`}
        >
            <>
                <div className="item-header">
                    <div className="item-drag-handle" {...attributes} {...listeners}>
                        <GripVertical size={20} />
                    </div>
                    <button
                        className="item-collapse-btn"
                        onClick={() => onToggleCollapse(item.id)}
                        title={isCollapsed ? "展開" : "折りたたむ"}
                    >
                        {isCollapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                    </button>
                    <div className="item-info">
                        <span className="item-index">#{index + 1}</span>
                        <div className="item-title-section">
                            {isEditing && editingItem ? (
                                <>
                                    <input
                                        type="text"
                                        className="item-title-input"
                                        value={editingItem.violation_category || ''}
                                        onChange={(e) => onUpdateEditingItem({ ...editingItem, violation_category: e.target.value })}
                                        placeholder="カテゴリ名"
                                    />
                                    <input
                                        type="text"
                                        className="item-id-input"
                                        value={editingItem.id || ''}
                                        onChange={(e) => onUpdateEditingItem({ ...editingItem, id: e.target.value })}
                                        placeholder="ID"
                                    />
                                </>
                            ) : (
                                <>
                                    <h4 className="item-title">{item.violation_category || '(タイトルなし)'}</h4>
                                    <span className="item-id">ID: {item.id}</span>
                                </>
                            )}
                        </div>
                        {rulesCount > 0 && (
                            <span className="item-rules-count">{rulesCount}個のルール</span>
                        )}
                    </div>
                    <div className="item-actions">
                        {isEditing && editingItem ? (
                            <>
                                <button
                                    className="btn-icon"
                                    onClick={onCancelEdit}
                                    title="キャンセル"
                                >
                                    <X size={16} />
                                </button>
                                <button
                                    className="btn-icon btn-icon-primary"
                                    onClick={() => onSave(item.id)}
                                    title="保存"
                                >
                                    <Save size={16} />
                                </button>
                            </>
                        ) : (
                            <>
                                <button
                                    className="btn-icon"
                                    onClick={() => onEdit(item)}
                                    title="編集"
                                >
                                    <Edit2 size={16} />
                                </button>
                                <button
                                    className="btn-icon btn-icon-danger"
                                    onClick={() => onDelete(item.id)}
                                    title="削除"
                                >
                                    <Trash2 size={16} />
                                </button>
                            </>
                        )}
                    </div>
                </div>
                {!isCollapsed && (
                    <>
                        {(isEditing || (normalizedItem.rules && normalizedItem.rules.length > 0)) && (
                            <SortableRulesList
                                rules={normalizedItem.rules || []}
                                categoryId={item.id}
                                getTypeDisplayName={getTypeDisplayName}
                                onRulesReorder={(newRules) => {
                                    onRulesReorder(item.id, newRules);
                                }}
                                collapsedRules={collapsedRules}
                                onToggleRuleCollapse={onToggleRuleCollapse}
                                isEditing={isEditing}
                                editingItem={isEditing ? editingItem : null}
                                localForbiddenImagePreviews={localForbiddenImagePreviews}
                                localRequiredImagePreviews={localRequiredImagePreviews}
                                persistedForbiddenImagePreviews={persistedForbiddenImagePreviews}
                                persistedForbiddenImageLoadFailed={persistedForbiddenImageLoadFailed}
                                onAddForbiddenImagesToRule={onAddForbiddenImagesToRule}
                                onRemoveForbiddenImageAssetFromRule={onRemoveForbiddenImageAssetFromRule}
                                onAddRequiredImagesToRule={onAddRequiredImagesToRule}
                                onRemoveRequiredImageAssetFromRule={onRemoveRequiredImageAssetFromRule}
                                onPreviewPersistedForbiddenImage={onPreviewPersistedForbiddenImage}
                                onUpdateRule={(ruleIndex, updatedRule) => {
                                    if (isEditing && editingItem) {
                                        // editingItem is already normalized when set in handleEditItem
                                        const currentRules = editingItem.rules || [];
                                        const newRules = [...currentRules];
                                        newRules[ruleIndex] = updatedRule;
                                        onUpdateEditingItem({ ...editingItem, rules: newRules });
                                    }
                                }}
                                onAddRule={() => {
                                    if (isEditing && editingItem) {
                                        const currentRules = editingItem.rules || [];
                                        const newRule: GuidelineRule = {
                                            id: `rule_${Date.now()}`,
                                            name: '',
                                            type: 'text_check',
                                            patrol_source: 'all_page',
                                            violation_check_prompt: '',
                                            existence_check_prompt: '',
                                        };
                                        const newRules = [...currentRules, newRule];
                                        onUpdateEditingItem({ ...editingItem, rules: newRules });
                                    }
                                }}
                                onDeleteRule={(ruleIndex) => {
                                    if (isEditing && editingItem) {
                                        const currentRules = editingItem.rules || [];
                                        const newRules = currentRules.filter((_: GuidelineRule, i: number) => i !== ruleIndex);
                                        onUpdateEditingItem({ ...editingItem, rules: newRules });
                                    }
                                }}
                            />
                        )}
                        {null}
                        {item.detail && !normalizedItem.rules && (
                            <div className="item-detail-section">
                                <h5>詳細</h5>
                                {isEditing && editingItem ? (
                                    <textarea
                                        className="item-detail-input"
                                        value={editingItem.detail || ''}
                                        onChange={(e) => onUpdateEditingItem({ ...editingItem, detail: e.target.value })}
                                        rows={3}
                                    />
                                ) : (
                                    <p className="item-detail">{item.detail}</p>
                                )}
                            </div>
                        )}
                    </>
                )}
            </>
        </div>
    );
};

// Sortable Rules List Component
interface SortableRulesListProps {
    rules: GuidelineRule[];
    categoryId: string;
    getTypeDisplayName: (type: string) => string;
    onRulesReorder: (newRules: GuidelineRule[]) => void;
    collapsedRules: Set<string>;
    onToggleRuleCollapse: (ruleKey: string) => void;
    isEditing?: boolean;
    editingItem?: GuidelineItem | null;
    onUpdateRule?: (ruleIndex: number, rule: GuidelineRule) => void;
    onAddRule?: () => void;
    onDeleteRule?: (ruleIndex: number) => void;
    localForbiddenImagePreviews: Record<string, string>;
    localRequiredImagePreviews: Record<string, string>;
    persistedForbiddenImagePreviews: Record<string, string>;
    persistedForbiddenImageLoadFailed: Record<string, boolean>;
    onAddForbiddenImagesToRule: (ruleIndex: number, files: FileList | null) => void;
    onRemoveForbiddenImageAssetFromRule: (ruleIndex: number, assetId: string) => void;
    onAddRequiredImagesToRule: (ruleIndex: number, files: FileList | null) => void;
    onRemoveRequiredImageAssetFromRule: (ruleIndex: number, assetId: string) => void;
    onPreviewPersistedForbiddenImage: (filePath: string) => void;
}

const SortableRulesList: React.FC<SortableRulesListProps> = ({
    rules,
    categoryId,
    getTypeDisplayName,
    onRulesReorder,
    collapsedRules,
    onToggleRuleCollapse,
    isEditing = false,
    editingItem = null,
    onUpdateRule = () => { },
    onAddRule,
    onDeleteRule,
    localForbiddenImagePreviews,
    localRequiredImagePreviews,
    persistedForbiddenImagePreviews,
    persistedForbiddenImageLoadFailed,
    onAddForbiddenImagesToRule,
    onRemoveForbiddenImageAssetFromRule,
    onAddRequiredImagesToRule,
    onRemoveRequiredImageAssetFromRule,
    onPreviewPersistedForbiddenImage,
}) => {
    const handleRuleDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (over && active.id !== over.id) {
            // Match IDs used in SortableContext (rule.id || `rule_${idx}`)
            const oldIndex = rules.findIndex((rule, idx) => {
                const ruleId = rule.id || `rule_${idx}`;
                return ruleId === active.id.toString();
            });
            const newIndex = rules.findIndex((rule, idx) => {
                const ruleId = rule.id || `rule_${idx}`;
                return ruleId === over.id.toString();
            });
            if (oldIndex !== -1 && newIndex !== -1) {
                const newRules = arrayMove(rules, oldIndex, newIndex);
                onRulesReorder(newRules);
            }
        }
    };

    const sensors = useSensors(
        useSensor(PointerSensor),
        useSensor(KeyboardSensor, {
            coordinateGetter: sortableKeyboardCoordinates,
        })
    );

    return (
        <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleRuleDragEnd}
        >
            <SortableContext
                items={rules.map((rule, idx) => rule.id || `rule_${idx}`)}
                strategy={verticalListSortingStrategy}
            >
                <div className="item-rules-details">
                    {rules.map((rule, ruleIndex) => {
                        const ruleKey = `${categoryId}_${rule.id || ruleIndex}`;
                        return (
                            <SortableRule
                                key={rule.id || `rule_${ruleIndex}`}
                                rule={rule}
                                ruleIndex={ruleIndex}
                                ruleKey={ruleKey}
                                isCollapsed={collapsedRules.has(ruleKey)}
                                onToggleCollapse={onToggleRuleCollapse}
                                getTypeDisplayName={getTypeDisplayName}
                                isEditing={isEditing}
                                onUpdateRule={(updatedRule) => {
                                    if (onUpdateRule) {
                                        onUpdateRule(ruleIndex, updatedRule);
                                    }
                                }}
                                onDeleteRule={onDeleteRule ? () => onDeleteRule(ruleIndex) : undefined}
                                localForbiddenImagePreviews={localForbiddenImagePreviews}
                                localRequiredImagePreviews={localRequiredImagePreviews}
                                persistedForbiddenImagePreviews={persistedForbiddenImagePreviews}
                                persistedForbiddenImageLoadFailed={persistedForbiddenImageLoadFailed}
                                onAddForbiddenImagesToRule={onAddForbiddenImagesToRule}
                                onRemoveForbiddenImageAssetFromRule={onRemoveForbiddenImageAssetFromRule}
                                onAddRequiredImagesToRule={onAddRequiredImagesToRule}
                                onRemoveRequiredImageAssetFromRule={onRemoveRequiredImageAssetFromRule}
                                onPreviewPersistedForbiddenImage={onPreviewPersistedForbiddenImage}
                            />
                        );
                    })}
                    {isEditing && onAddRule && (
                        <button
                            className="btn btn-secondary btn-sm"
                            onClick={onAddRule}
                            style={{ marginTop: '12px' }}
                        >
                            <Plus size={14} />
                            ルールを追加
                        </button>
                    )}
                </div>
            </SortableContext>
        </DndContext>
    );
};

// Sortable Rule Component
interface SortableRuleProps {
    rule: GuidelineRule;
    ruleIndex: number;
    ruleKey: string;
    isCollapsed: boolean;
    onToggleCollapse: (ruleKey: string) => void;
    getTypeDisplayName: (type: string) => string;
    isEditing?: boolean;
    onUpdateRule?: (rule: GuidelineRule) => void;
    onDeleteRule?: () => void;
    localForbiddenImagePreviews: Record<string, string>;
    localRequiredImagePreviews: Record<string, string>;
    persistedForbiddenImagePreviews: Record<string, string>;
    persistedForbiddenImageLoadFailed: Record<string, boolean>;
    onAddForbiddenImagesToRule: (ruleIndex: number, files: FileList | null) => void;
    onRemoveForbiddenImageAssetFromRule: (ruleIndex: number, assetId: string) => void;
    onAddRequiredImagesToRule: (ruleIndex: number, files: FileList | null) => void;
    onRemoveRequiredImageAssetFromRule: (ruleIndex: number, assetId: string) => void;
    onPreviewPersistedForbiddenImage: (filePath: string) => void;
}

const SortableRule: React.FC<SortableRuleProps> = ({
    rule,
    ruleIndex,
    ruleKey,
    isCollapsed,
    onToggleCollapse,
    getTypeDisplayName,
    isEditing = false,
    onUpdateRule = () => { },
    onDeleteRule,
    localForbiddenImagePreviews,
    localRequiredImagePreviews,
    persistedForbiddenImagePreviews,
    persistedForbiddenImageLoadFailed,
    onAddForbiddenImagesToRule,
    onRemoveForbiddenImageAssetFromRule,
    onAddRequiredImagesToRule,
    onRemoveRequiredImageAssetFromRule,
    onPreviewPersistedForbiddenImage,
}) => {
    const {
        attributes,
        listeners,
        setNodeRef,
        transform,
        transition,
        isDragging
    } = useSortable({ id: rule.id || `rule_${ruleIndex}` });

    const [showTypeDropdown, setShowTypeDropdown] = useState(false);
    const [showPatrolSourceDropdown, setShowPatrolSourceDropdown] = useState(false);
    const typeDropdownRef = React.useRef<HTMLDivElement>(null);
    const patrolSourceDropdownRef = React.useRef<HTMLDivElement>(null);

    // Close dropdowns when clicking outside
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (typeDropdownRef.current && !typeDropdownRef.current.contains(event.target as Node)) {
                setShowTypeDropdown(false);
            }
            if (patrolSourceDropdownRef.current && !patrolSourceDropdownRef.current.contains(event.target as Node)) {
                setShowPatrolSourceDropdown(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    // Close dropdowns when rule values change (to ensure UI reflects current state)
    useEffect(() => {
        setShowTypeDropdown(false);
        setShowPatrolSourceDropdown(false);
    }, [rule.type, rule.patrol_source]);

    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1
    };

    const typeOptions = [
        { value: 'text_check', label: 'テキストチェック' },
        { value: 'forbidden_regular_expression_check', label: '禁止表現チェック' },
        { value: 'musthave_regular_expression_check', label: '必須表現チェック' },
        { value: 'forbidden_image', label: '禁止画像チェック' },
        { value: 'required_image_match', label: '必要画像一致チェック' },
    ];

    const patrolSourceOptions = [
        { value: 'all_page', label: 'ページ全体' },
        { value: 'page_chunk', label: 'ページのチャンク単位' }
    ];

    // Helper function to determine which fields should be shown based on rule type
    const shouldShowField = (fieldName: string): boolean => {
        const ruleType = rule.type || 'text_check';

        if (fieldName === 'patrol_source') {
            // All three types require patrol_source
            return ruleType === 'text_check' ||
                ruleType === 'forbidden_regular_expression_check' ||
                ruleType === 'musthave_regular_expression_check';
        }

        if (fieldName === 'violation_check_prompt' || fieldName === 'existence_check_prompt') {
            // Only text_check requires these
            return ruleType === 'text_check';
        }

        if (fieldName === 'forbidden_regular_expressions' || fieldName === 'forbidden_detect_message') {
            // Only forbidden_regular_expression_check requires these
            return ruleType === 'forbidden_regular_expression_check';
        }

        if (fieldName === 'musthave_regular_expressions' || fieldName === 'musthave_not_detect_message') {
            // Only musthave_regular_expression_check requires these
            return ruleType === 'musthave_regular_expression_check';
        }

        if (fieldName === 'regular_expression_conditions') {
            // Both forbidden and musthave types can have conditions
            return ruleType === 'forbidden_regular_expression_check' ||
                ruleType === 'musthave_regular_expression_check';
        }

        return false;
    };

    // Handle type change - clear irrelevant fields
    const handleTypeChange = (newType: string) => {
        const updatedRule: GuidelineRule = { ...rule, type: newType };

        // Clear fields that are not relevant to the new type
        if (newType === 'text_check') {
            // Clear forbidden and musthave fields
            delete updatedRule.forbidden_regular_expressions;
            delete updatedRule.forbidden_detect_message;
            delete updatedRule.musthave_regular_expressions;
            delete updatedRule.musthave_not_detect_message;
            delete (updatedRule as any).forbidden_image_assets;
            delete (updatedRule as any).similarity_threshold;
            delete (updatedRule as any).min_good_matches;
            delete (updatedRule as any).min_score;
            delete (updatedRule as any).min_inlier_ratio;
            // Ensure patrol_source is set
            if (!updatedRule.patrol_source) {
                updatedRule.patrol_source = 'all_page';
            }
        } else if (newType === 'forbidden_regular_expression_check') {
            // Clear text_check and musthave fields
            delete updatedRule.violation_check_prompt;
            delete updatedRule.existence_check_prompt;
            delete updatedRule.skip_existence_check;
            delete updatedRule.musthave_regular_expressions;
            delete updatedRule.musthave_not_detect_message;
            delete (updatedRule as any).forbidden_image_assets;
            delete (updatedRule as any).similarity_threshold;
            delete (updatedRule as any).min_good_matches;
            delete (updatedRule as any).min_score;
            delete (updatedRule as any).min_inlier_ratio;
            // Initialize forbidden_regular_expressions if empty
            if (!updatedRule.forbidden_regular_expressions || updatedRule.forbidden_regular_expressions.length === 0) {
                updatedRule.forbidden_regular_expressions = [''];
            }
            // Ensure patrol_source is set
            if (!updatedRule.patrol_source) {
                updatedRule.patrol_source = 'all_page';
            }
        } else if (newType === 'musthave_regular_expression_check') {
            // Clear text_check and forbidden fields
            delete updatedRule.violation_check_prompt;
            delete updatedRule.existence_check_prompt;
            delete updatedRule.skip_existence_check;
            delete updatedRule.forbidden_regular_expressions;
            delete updatedRule.forbidden_detect_message;
            delete (updatedRule as any).forbidden_image_assets;
            delete (updatedRule as any).similarity_threshold;
            delete (updatedRule as any).min_good_matches;
            delete (updatedRule as any).min_score;
            delete (updatedRule as any).min_inlier_ratio;
            // Initialize musthave_regular_expressions if empty
            if (!updatedRule.musthave_regular_expressions || updatedRule.musthave_regular_expressions.length === 0) {
                updatedRule.musthave_regular_expressions = [''];
            }
            // Ensure patrol_source is set
            if (!updatedRule.patrol_source) {
                updatedRule.patrol_source = 'all_page';
            }
        } else if (newType === 'forbidden_image') {
            // Clear non-image fields
            delete updatedRule.violation_check_prompt;
            delete updatedRule.existence_check_prompt;
            delete updatedRule.skip_existence_check;
            delete updatedRule.forbidden_regular_expressions;
            delete updatedRule.forbidden_detect_message;
            delete updatedRule.musthave_regular_expressions;
            delete updatedRule.musthave_not_detect_message;
            delete updatedRule.regular_expression_conditions;
            delete updatedRule.patrol_source;

            if (!(updatedRule as any).forbidden_image_assets || (updatedRule as any).forbidden_image_assets.length === 0) {
                (updatedRule as any).forbidden_image_assets = [];
            }
        } else if (newType === 'required_image_match') {
            // Clear non-image fields
            delete updatedRule.violation_check_prompt;
            delete updatedRule.existence_check_prompt;
            delete updatedRule.skip_existence_check;
            delete updatedRule.forbidden_regular_expressions;
            delete updatedRule.forbidden_detect_message;
            delete updatedRule.musthave_regular_expressions;
            delete updatedRule.musthave_not_detect_message;
            delete updatedRule.regular_expression_conditions;
            delete updatedRule.patrol_source;

            delete (updatedRule as any).forbidden_image_assets;
            delete (updatedRule as any).similarity_threshold;

            if (!(updatedRule as any).required_image_assets || (updatedRule as any).required_image_assets.length === 0) {
                (updatedRule as any).required_image_assets = [];
            }
            if (!(updatedRule as any).target_image_extraction_prompt) {
                (updatedRule as any).target_image_extraction_prompt = '';
            }
        }

        onUpdateRule(updatedRule);
    };

    return (
        <div ref={setNodeRef} style={style} className={`rule-detail-card ${isDragging ? 'dragging' : ''}`}>
            <div className="rule-detail-header">
                <div className="rule-drag-handle" {...attributes} {...listeners}>
                    <GripVertical size={16} />
                </div>
                <button
                    className="rule-collapse-btn"
                    onClick={() => onToggleCollapse(ruleKey)}
                    title={isCollapsed ? "展開" : "折りたたむ"}
                >
                    {isCollapsed ? <ChevronRight size={18} /> : <ChevronDown size={18} />}
                </button>
                <div className="rule-title-wrapper">
                    <h5 className="rule-detail-title">
                        {isEditing ? (
                            <div className="rule-name-edit-wrapper">
                                <span className="rule-number">ルール {ruleIndex + 1}:</span>
                                <input
                                    type="text"
                                    className="rule-name-input"
                                    value={rule.name || ''}
                                    onChange={(e) => onUpdateRule({ ...rule, name: e.target.value })}
                                    placeholder="ルール名を入力"
                                    onClick={(e) => e.stopPropagation()}
                                />
                            </div>
                        ) : (
                            <>
                                <span className="rule-number">ルール {ruleIndex + 1}</span>
                                {rule.name && (
                                    <>
                                        <span className="rule-name-separator">:</span>
                                        <span className="rule-name-text">{rule.name}</span>
                                    </>
                                )}
                            </>
                        )}
                    </h5>
                </div>
                {isEditing && onDeleteRule && (
                    <button
                        className="btn-icon btn-icon-danger btn-icon-sm"
                        onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (window.confirm('このルールを削除しますか？')) {
                                onDeleteRule();
                            }
                        }}
                        title="ルールを削除"
                    >
                        <Trash2 size={16} />
                    </button>
                )}
            </div>
            {!isCollapsed && (
                <div className="rule-detail-content">
                    <div className="rule-detail-field">
                        <label>タイプ:</label>
                        {isEditing ? (
                            <div className="dropdown-container" ref={typeDropdownRef}>
                                <button
                                    type="button"
                                    className={`dropdown-button rule-detail-dropdown ${rule.type ? 'selected' : ''}`}
                                    onClick={(e) => {
                                        e.preventDefault();
                                        e.stopPropagation();
                                        setShowTypeDropdown(!showTypeDropdown);
                                    }}
                                >
                                    <span>{typeOptions.find(opt => opt.value === rule.type)?.label || typeOptions[0]?.label || 'テキストチェック'}</span>
                                    <ChevronDown size={16} />
                                </button>
                                {showTypeDropdown && (
                                    <div className="dropdown-list">
                                        {typeOptions.map((option) => (
                                            <button
                                                key={option.value}
                                                type="button"
                                                className={`dropdown-item ${rule.type === option.value ? 'active' : ''}`}
                                                onClick={(e) => {
                                                    e.preventDefault();
                                                    e.stopPropagation();
                                                    handleTypeChange(option.value);
                                                    setShowTypeDropdown(false);
                                                }}
                                            >
                                                {option.label}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ) : (
                            <span>{getTypeDisplayName(rule.type || 'description')}</span>
                        )}
                    </div>
                    {shouldShowField('patrol_source') && (
                        <div className="rule-detail-field">
                            <label>パトロールソース:</label>
                            {isEditing ? (
                                <div className="dropdown-container" ref={patrolSourceDropdownRef}>
                                    <button
                                        type="button"
                                        className={`dropdown-button rule-detail-dropdown ${rule.patrol_source ? 'selected' : ''}`}
                                        onClick={() => setShowPatrolSourceDropdown(!showPatrolSourceDropdown)}
                                    >
                                        <span>{patrolSourceOptions.find(opt => opt.value === (rule.patrol_source || 'all_page'))?.label || patrolSourceOptions[0].label}</span>
                                        <ChevronDown size={16} />
                                    </button>
                                    {showPatrolSourceDropdown && (
                                        <div className="dropdown-list">
                                            {patrolSourceOptions.map((option) => (
                                                <button
                                                    key={option.value}
                                                    type="button"
                                                    className={`dropdown-item ${rule.patrol_source === option.value ? 'active' : ''}`}
                                                    onClick={(e) => {
                                                        e.preventDefault();
                                                        e.stopPropagation();
                                                        const updatedRule = { ...rule, patrol_source: option.value };
                                                        onUpdateRule(updatedRule);
                                                        setShowPatrolSourceDropdown(false);
                                                    }}
                                                >
                                                    {option.label}
                                                </button>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            ) : (
                                rule.patrol_source && <span>{rule.patrol_source === 'all_page' ? 'ページ全体' : 'ページのチャンク単位'}</span>
                            )}
                        </div>
                    )}
                    {rule.type === 'forbidden_image' && (
                        <div className="rule-detail-field">
                            <div className="forbidden-image-card">
                                <div className="forbidden-image-toolbar">
                                    <div className="forbidden-image-thresholdRow">
                                        <label>min good_matches</label>
                                        <input
                                            type="number"
                                            min={0}
                                            step={1}
                                            value={isEditing ? ((rule as any).min_good_matches ?? '') : ((rule as any).min_good_matches ?? 50)}
                                            placeholder="50"
                                            disabled={!isEditing}
                                            onFocus={(e) => {
                                                // Typing should replace the whole value (avoid "060" style leading zeros).
                                                e.currentTarget.select();
                                            }}
                                            onKeyDown={(e) => {
                                                // If the field currently shows "0", typing a digit should overwrite it.
                                                if (!isEditing) return;
                                                if (e.key.length === 1 && /[0-9]/.test(e.key) && e.currentTarget.value === '0') {
                                                    e.currentTarget.value = '';
                                                }
                                            }}
                                            onChange={(e) => {
                                                if (!isEditing) return;
                                                // Allow empty value (unset -> backend default).
                                                if (e.currentTarget.value === '') {
                                                    const nextRule: any = { ...(rule as any) };
                                                    delete nextRule.min_good_matches;
                                                    onUpdateRule(nextRule);
                                                    return;
                                                }
                                                const next = e.currentTarget.valueAsNumber;
                                                if (Number.isNaN(next)) return;
                                                onUpdateRule(
                                                    { ...(rule as any), min_good_matches: Math.max(0, Math.trunc(next)) } as any
                                                );
                                            }}
                                            className="rule-detail-input forbidden-image-threshold"
                                        />
                                    </div>
                                    <div className="forbidden-image-thresholdRow">
                                        <label>min score</label>
                                        <input
                                            type="number"
                                            min={0}
                                            max={1}
                                            step={0.01}
                                            value={isEditing ? ((rule as any).min_score ?? '') : ((rule as any).min_score ?? 0.1)}
                                            placeholder="0.1"
                                            disabled={!isEditing}
                                            onFocus={(e) => {
                                                e.currentTarget.select();
                                            }}
                                            onChange={(e) => {
                                                if (!isEditing) return;
                                                if (e.currentTarget.value === '') {
                                                    const nextRule: any = { ...(rule as any) };
                                                    delete nextRule.min_score;
                                                    onUpdateRule(nextRule);
                                                    return;
                                                }
                                                const next = e.currentTarget.valueAsNumber;
                                                if (Number.isNaN(next)) return;
                                                onUpdateRule(
                                                    { ...(rule as any), min_score: next } as any
                                                );
                                            }}
                                            className="rule-detail-input forbidden-image-threshold"
                                        />
                                    </div>
                                    <div className="forbidden-image-thresholdRow">
                                        <label>min inlier_ratio</label>
                                        <input
                                            type="number"
                                            min={0}
                                            max={1}
                                            step={0.01}
                                            value={isEditing ? ((rule as any).min_inlier_ratio ?? '') : ((rule as any).min_inlier_ratio ?? 0.4)}
                                            placeholder="0.4"
                                            disabled={!isEditing}
                                            onFocus={(e) => {
                                                e.currentTarget.select();
                                            }}
                                            onChange={(e) => {
                                                if (!isEditing) return;
                                                if (e.currentTarget.value === '') {
                                                    const nextRule: any = { ...(rule as any) };
                                                    delete nextRule.min_inlier_ratio;
                                                    onUpdateRule(nextRule);
                                                    return;
                                                }
                                                const next = e.currentTarget.valueAsNumber;
                                                if (Number.isNaN(next)) return;
                                                onUpdateRule(
                                                    { ...(rule as any), min_inlier_ratio: next } as any
                                                );
                                            }}
                                            className="rule-detail-input forbidden-image-threshold"
                                        />
                                    </div>
                                </div>
                                <div className="forbidden-image-divider" />
                                <div className="forbidden-image-sectionTitle">禁止画像リスト</div>
                                <div
                                    className="forbidden-image-grid"
                                >
                                {(((rule as any).forbidden_image_assets as any[]) || []).map((asset) => {
                                    const filePath = String(asset.file_path || '');
                                    const isLocal = filePath.startsWith('localref://');
                                    const localId = isLocal ? filePath.replace('localref://', '') : '';
                                    const localPreview = isLocal ? localForbiddenImagePreviews[localId] : undefined;
                                    const persistedPreview = !isLocal ? persistedForbiddenImagePreviews[filePath] : undefined;
                                    const displayName = String(asset.original_filename || '').trim() || '画像';
                                    return (
                                        <div key={asset.id} className="forbidden-image-item">
                                            <div className="forbidden-image-thumbWrap">
                                                {isEditing && (
                                                    <button
                                                        type="button"
                                                        onClick={() => onRemoveForbiddenImageAssetFromRule(ruleIndex, asset.id)}
                                                        title="削除"
                                                        className="forbidden-image-delete"
                                                    >
                                                        ×
                                                    </button>
                                                )}
                                            <div className="forbidden-image-thumb">
                                                {localPreview || persistedPreview ? (
                                                    <img
                                                        src={localPreview || persistedPreview}
                                                        className="forbidden-image-img"
                                                        alt={displayName}
                                                    />
                                                ) : isLocal ? (
                                                    <span className="forbidden-image-thumbPlaceholder">—</span>
                                                ) : persistedForbiddenImageLoadFailed[filePath] ? (
                                                    <button
                                                        type="button"
                                                        className="btn btn-secondary btn-sm forbidden-image-retry"
                                                        onClick={() => onPreviewPersistedForbiddenImage(filePath)}
                                                    >
                                                        再試行
                                                    </button>
                                                ) : (
                                                    <Loader2 className="spinner forbidden-image-thumbSpinner" size={22} aria-label="読み込み中" />
                                                )}
                                            </div>
                                        </div>
                                            <div
                                                title={filePath}
                                                className="forbidden-image-caption"
                                            >
                                                {displayName}
                                            </div>
                                        </div>
                                    );
                                })}
                                    {isEditing && (
                                        <label
                                            className="forbidden-image-add"
                                            title="画像追加"
                                        >
                                            <input
                                                type="file"
                                                accept="image/png,image/jpeg,image/webp"
                                                multiple
                                                style={{ display: 'none' }}
                                                onChange={(e) => onAddForbiddenImagesToRule(ruleIndex, e.target.files)}
                                            />
                                            <div className="forbidden-image-addTile">
                                                <Plus size={18} />
                                                <span style={{ fontSize: 13, fontWeight: 600 }}>追加</span>
                                            </div>
                                        </label>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {rule.type === 'required_image_match' && (
                        <div className="rule-detail-field">
                            <div className="forbidden-image-card">
                                <div className="forbidden-image-sectionTitle">対象画像抽出プロンプト</div>
                                <div className="forbidden-image-toolbar">
                                    <div className="forbidden-image-thresholdRow" style={{ flex: 1 }}>
                                        <textarea
                                            className="rule-detail-textarea"
                                            value={(rule as any).target_image_extraction_prompt ?? ''}
                                            disabled={!isEditing}
                                            onChange={(e) => {
                                                if (!isEditing) return;
                                                onUpdateRule({ ...(rule as any), target_image_extraction_prompt: e.target.value } as any);
                                            }}
                                            placeholder="HTMLから対象の<img>要素（data-unique-id）を抽出する指示を書いてください。出力は unique_ids のJSON。"
                                            rows={4}
                                        />
                                    </div>
                                </div>
                            </div>
                            <div className="forbidden-image-card" style={{ marginTop: 12 }}>
                                <div className="forbidden-image-sectionTitle">参照画像・一致判定</div>
                                <div className="forbidden-image-toolbar">
                                    <div className="forbidden-image-thresholdRow">
                                        <label>min good_matches</label>
                                        <input
                                            type="number"
                                            min={0}
                                            step={1}
                                            value={isEditing ? ((rule as any).min_good_matches ?? '') : ((rule as any).min_good_matches ?? 50)}
                                            placeholder="50"
                                            disabled={!isEditing}
                                            onFocus={(e) => e.currentTarget.select()}
                                            onChange={(e) => {
                                                if (!isEditing) return;
                                                if (e.currentTarget.value === '') {
                                                    const nextRule: any = { ...(rule as any) };
                                                    delete nextRule.min_good_matches;
                                                    onUpdateRule(nextRule);
                                                    return;
                                                }
                                                const next = e.currentTarget.valueAsNumber;
                                                if (Number.isNaN(next)) return;
                                                onUpdateRule({ ...(rule as any), min_good_matches: Math.max(0, Math.trunc(next)) } as any);
                                            }}
                                            className="rule-detail-input forbidden-image-threshold"
                                        />
                                    </div>
                                    <div className="forbidden-image-thresholdRow">
                                        <label>min score</label>
                                        <input
                                            type="number"
                                            min={0}
                                            max={1}
                                            step={0.01}
                                            value={isEditing ? ((rule as any).min_score ?? '') : ((rule as any).min_score ?? 0.1)}
                                            placeholder="0.1"
                                            disabled={!isEditing}
                                            onFocus={(e) => e.currentTarget.select()}
                                            onChange={(e) => {
                                                if (!isEditing) return;
                                                if (e.currentTarget.value === '') {
                                                    const nextRule: any = { ...(rule as any) };
                                                    delete nextRule.min_score;
                                                    onUpdateRule(nextRule);
                                                    return;
                                                }
                                                const next = e.currentTarget.valueAsNumber;
                                                if (Number.isNaN(next)) return;
                                                onUpdateRule({ ...(rule as any), min_score: next } as any);
                                            }}
                                            className="rule-detail-input forbidden-image-threshold"
                                        />
                                    </div>
                                    <div className="forbidden-image-thresholdRow">
                                        <label>min inlier_ratio</label>
                                        <input
                                            type="number"
                                            min={0}
                                            max={1}
                                            step={0.01}
                                            value={isEditing ? ((rule as any).min_inlier_ratio ?? '') : ((rule as any).min_inlier_ratio ?? 0.4)}
                                            placeholder="0.4"
                                            disabled={!isEditing}
                                            onFocus={(e) => e.currentTarget.select()}
                                            onChange={(e) => {
                                                if (!isEditing) return;
                                                if (e.currentTarget.value === '') {
                                                    const nextRule: any = { ...(rule as any) };
                                                    delete nextRule.min_inlier_ratio;
                                                    onUpdateRule(nextRule);
                                                    return;
                                                }
                                                const next = e.currentTarget.valueAsNumber;
                                                if (Number.isNaN(next)) return;
                                                onUpdateRule({ ...(rule as any), min_inlier_ratio: next } as any);
                                            }}
                                            className="rule-detail-input forbidden-image-threshold"
                                        />
                                    </div>
                                </div>
                                <div className="forbidden-image-divider" />
                                <div className="forbidden-image-sectionTitle">参照画像リスト</div>
                                <div className="forbidden-image-grid">
                                    {(((rule as any).required_image_assets as any[]) || []).map((asset) => {
                                        const filePath = String(asset.file_path || '');
                                        const isLocal = filePath.startsWith('localref://');
                                        const localId = isLocal ? filePath.replace('localref://', '') : '';
                                        const localPreview = isLocal ? localRequiredImagePreviews[localId] : undefined;
                                        const persistedPreview = !isLocal ? persistedForbiddenImagePreviews[filePath] : undefined;
                                        const displayName = String(asset.original_filename || '').trim() || '画像';
                                        return (
                                            <div key={asset.id} className="forbidden-image-item">
                                                <div className="forbidden-image-thumbWrap">
                                                    {isEditing && (
                                                        <button
                                                            type="button"
                                                            onClick={() => onRemoveRequiredImageAssetFromRule(ruleIndex, asset.id)}
                                                            title="削除"
                                                            className="forbidden-image-delete"
                                                        >
                                                            ×
                                                        </button>
                                                    )}
                                                    <div className="forbidden-image-thumb">
                                                        {localPreview || persistedPreview ? (
                                                            <img
                                                                src={localPreview || persistedPreview}
                                                                className="forbidden-image-img"
                                                                alt={displayName}
                                                            />
                                                        ) : isLocal ? (
                                                            <span className="forbidden-image-thumbPlaceholder">—</span>
                                                        ) : persistedForbiddenImageLoadFailed[filePath] ? (
                                                            <button
                                                                type="button"
                                                                className="btn btn-secondary btn-sm forbidden-image-retry"
                                                                onClick={() => onPreviewPersistedForbiddenImage(filePath)}
                                                            >
                                                                再試行
                                                            </button>
                                                        ) : (
                                                            <Loader2 className="spinner forbidden-image-thumbSpinner" size={22} aria-label="読み込み中" />
                                                        )}
                                                    </div>
                                                </div>
                                                <div title={filePath} className="forbidden-image-caption">
                                                    {displayName}
                                                </div>
                                            </div>
                                        );
                                    })}
                                    {isEditing && (
                                        <label className="forbidden-image-add" title="画像追加">
                                            <input
                                                type="file"
                                                accept="image/png,image/jpeg,image/webp"
                                                multiple
                                                style={{ display: 'none' }}
                                                onChange={(e) => onAddRequiredImagesToRule(ruleIndex, e.target.files)}
                                            />
                                            <div className="forbidden-image-addTile">
                                                <Plus size={18} />
                                                <span style={{ fontSize: 13, fontWeight: 600 }}>追加</span>
                                            </div>
                                        </label>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                    {shouldShowField('violation_check_prompt') && (
                        <div className="rule-detail-field">
                            <label>違反チェックプロンプト:</label>
                            {isEditing ? (
                                <textarea
                                    className="rule-detail-textarea"
                                    value={rule.violation_check_prompt || ''}
                                    onChange={(e) => onUpdateRule({ ...rule, violation_check_prompt: e.target.value })}
                                    placeholder="違反チェックプロンプト"
                                    rows={4}
                                />
                            ) : (
                                shouldShowField('violation_check_prompt') && rule.violation_check_prompt && <div className="rule-detail-text">{rule.violation_check_prompt}</div>
                            )}
                        </div>
                    )}
                    {shouldShowField('existence_check_prompt') && (
                        <div className="rule-detail-field">
                            <label>存在チェックプロンプト:</label>
                            {isEditing && (
                                <label className="rule-detail-checkbox-label">
                                    <input
                                        type="checkbox"
                                        checked={!!rule.skip_existence_check}
                                        onChange={(e) => {
                                            const checked = e.target.checked;
                                            onUpdateRule({
                                                ...rule,
                                                skip_existence_check: checked,
                                                existence_check_prompt: checked ? '' : rule.existence_check_prompt,
                                            });
                                        }}
                                    />
                                    存在チェックをスキップする（違反チェックプロンプトのみで判定）
                                </label>
                            )}
                            {isEditing ? (
                                <textarea
                                    className="rule-detail-textarea"
                                    value={rule.existence_check_prompt || ''}
                                    onChange={(e) => onUpdateRule({ ...rule, existence_check_prompt: e.target.value })}
                                    placeholder="存在チェックプロンプト"
                                    rows={4}
                                    disabled={!!rule.skip_existence_check}
                                />
                            ) : (
                                shouldShowField('existence_check_prompt') && rule.existence_check_prompt && <div className="rule-detail-text">{rule.existence_check_prompt}</div>
                            )}
                        </div>
                    )}
                    {shouldShowField('forbidden_regular_expressions') && (
                        <div className="rule-detail-field">
                            <label>禁止正規表現: <span className="required">*</span></label>
                            {isEditing ? (
                                <div className="rule-detail-list">
                                    {(rule.forbidden_regular_expressions && rule.forbidden_regular_expressions.length > 0) ? (
                                        rule.forbidden_regular_expressions.map((expr, exprIndex) => (
                                            <div key={exprIndex} className="rule-detail-list-item">
                                                <input
                                                    type="text"
                                                    className="rule-detail-input-inline rule-detail-regex-input"
                                                    value={expr}
                                                    onChange={(e) => {
                                                        const newExprs = [...(rule.forbidden_regular_expressions || [])];
                                                        newExprs[exprIndex] = e.target.value;
                                                        onUpdateRule({ ...rule, forbidden_regular_expressions: newExprs });
                                                    }}
                                                    placeholder="例: [\\u3040-\\u30FF]+(禁止|NG)"
                                                />
                                                <button
                                                    className="btn-icon btn-icon-danger btn-icon-sm"
                                                    onClick={() => {
                                                        const newExprs = (rule.forbidden_regular_expressions || []).filter((_: string, i: number) => i !== exprIndex);
                                                        onUpdateRule({ ...rule, forbidden_regular_expressions: newExprs });
                                                    }}
                                                    title="削除"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="rule-detail-empty-state">
                                            <span className="rule-detail-empty-text">正規表現が登録されていません</span>
                                        </div>
                                    )}
                                    <button
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => {
                                            const newExprs = [...(rule.forbidden_regular_expressions || []), ''];
                                            onUpdateRule({ ...rule, forbidden_regular_expressions: newExprs });
                                        }}
                                    >
                                        <Plus size={14} />
                                        追加
                                    </button>
                                </div>
                            ) : (
                                shouldShowField('forbidden_regular_expressions') && rule.forbidden_regular_expressions && rule.forbidden_regular_expressions.length > 0 && (
                                    <div className="rule-detail-list">
                                        {rule.forbidden_regular_expressions.map((expr, exprIndex) => (
                                            <div key={exprIndex} className="rule-detail-list-item">
                                                <code>{expr}</code>
                                            </div>
                                        ))}
                                    </div>
                                )
                            )}
                        </div>
                    )}
                    {shouldShowField('musthave_regular_expressions') && (
                        <div className="rule-detail-field">
                            <label>必須正規表現: <span className="required">*</span></label>
                            {isEditing ? (
                                <div className="rule-detail-list">
                                    {(rule.musthave_regular_expressions && rule.musthave_regular_expressions.length > 0) ? (
                                        rule.musthave_regular_expressions.map((expr, exprIndex) => (
                                            <div key={exprIndex} className="rule-detail-list-item">
                                                <input
                                                    type="text"
                                                    className="rule-detail-input-inline rule-detail-regex-input"
                                                    value={expr}
                                                    onChange={(e) => {
                                                        const newExprs = [...(rule.musthave_regular_expressions || [])];
                                                        newExprs[exprIndex] = e.target.value;
                                                        onUpdateRule({ ...rule, musthave_regular_expressions: newExprs });
                                                    }}
                                                    placeholder="例: [\\u3040-\\u30FF]+(広告|PR|AD)"
                                                />
                                                <button
                                                    className="btn-icon btn-icon-danger btn-icon-sm"
                                                    onClick={() => {
                                                        const newExprs = (rule.musthave_regular_expressions || []).filter((_: string, i: number) => i !== exprIndex);
                                                        onUpdateRule({ ...rule, musthave_regular_expressions: newExprs });
                                                    }}
                                                    title="削除"
                                                >
                                                    <Trash2 size={14} />
                                                </button>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="rule-detail-empty-state">
                                            <span className="rule-detail-empty-text">正規表現が登録されていません</span>
                                        </div>
                                    )}
                                    <button
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => {
                                            const newExprs = [...(rule.musthave_regular_expressions || []), ''];
                                            onUpdateRule({ ...rule, musthave_regular_expressions: newExprs });
                                        }}
                                    >
                                        <Plus size={14} />
                                        追加
                                    </button>
                                </div>
                            ) : (
                                shouldShowField('musthave_regular_expressions') && rule.musthave_regular_expressions && rule.musthave_regular_expressions.length > 0 && (
                                    <div className="rule-detail-list">
                                        {rule.musthave_regular_expressions.map((expr, exprIndex) => (
                                            <div key={exprIndex} className="rule-detail-list-item">
                                                <code>{expr}</code>
                                            </div>
                                        ))}
                                    </div>
                                )
                            )}
                        </div>
                    )}
                    {shouldShowField('regular_expression_conditions') && (
                        <div className="rule-detail-field">
                            <label>正規表現条件: <span className="optional">(任意)</span></label>
                            {isEditing ? (
                                <div className="rule-detail-list">
                                    {(rule.regular_expression_conditions && rule.regular_expression_conditions.length > 0) ? (
                                        rule.regular_expression_conditions?.map((condition: GuidelineRegularExpressionCondition, condIndex: number) => (
                                            <div key={condIndex} className="rule-detail-condition-card">
                                                <div className="rule-detail-condition-header">
                                                    <span className="rule-detail-condition-title">条件 {condIndex + 1}</span>
                                                    <button
                                                        className="btn-icon btn-icon-danger btn-icon-sm"
                                                        onClick={() => {
                                                            const newConditions = (rule.regular_expression_conditions || []).filter((_: GuidelineRegularExpressionCondition, i: number) => i !== condIndex);
                                                            onUpdateRule({ ...rule, regular_expression_conditions: newConditions });
                                                        }}
                                                        title="条件を削除"
                                                    >
                                                        <Trash2 size={14} />
                                                    </button>
                                                </div>
                                                <div className="rule-detail-condition-content">
                                                    <div className="rule-detail-field-inline">
                                                        <label>タイプ:</label>
                                                        <select
                                                            className="rule-detail-input"
                                                            value={condition.type || 'allow_if'}
                                                            onChange={(e) => {
                                                                const newConditions = [...(rule.regular_expression_conditions || [])];
                                                                newConditions[condIndex] = { ...condition, type: e.target.value };
                                                                onUpdateRule({ ...rule, regular_expression_conditions: newConditions });
                                                            }}
                                                        >
                                                            <option value="allow_if">allow_if</option>
                                                        </select>
                                                    </div>
                                                    <div className="rule-detail-field-inline">
                                                        <label>コンテキストウィンドウ: <span className="required">*</span></label>
                                                        <input
                                                            type="number"
                                                            className="rule-detail-input"
                                                            value={condition.context_window || ''}
                                                            onChange={(e) => {
                                                                const newConditions = [...(rule.regular_expression_conditions || [])];
                                                                newConditions[condIndex] = { ...condition, context_window: e.target.value ? parseInt(e.target.value) : undefined };
                                                                onUpdateRule({ ...rule, regular_expression_conditions: newConditions });
                                                            }}
                                                            placeholder="例: 50"
                                                            min="0"
                                                            required
                                                        />
                                                        <span className="rule-detail-helper-text">正規表現マッチの前後の文字数を指定します</span>
                                                    </div>
                                                    <div className="rule-detail-field-inline">
                                                        <label>ルール:</label>
                                                        <textarea
                                                            className="rule-detail-textarea"
                                                            value={condition.rule || ''}
                                                            onChange={(e) => {
                                                                const newConditions = [...(rule.regular_expression_conditions || [])];
                                                                newConditions[condIndex] = { ...condition, rule: e.target.value };
                                                                onUpdateRule({ ...rule, regular_expression_conditions: newConditions });
                                                            }}
                                                            placeholder="条件のルールを入力してください（例: 旧社名として記載されている場合はOK）"
                                                            rows={6}
                                                        />
                                                    </div>
                                                </div>
                                            </div>
                                        ))
                                    ) : (
                                        <div className="rule-detail-empty-state">
                                            <span className="rule-detail-empty-text">条件が登録されていません</span>
                                        </div>
                                    )}
                                    <button
                                        className="btn btn-secondary btn-sm"
                                        onClick={() => {
                                            const newConditions = [...(rule.regular_expression_conditions || []), { type: 'allow_if', rule: '', context_window: 0 }];
                                            onUpdateRule({ ...rule, regular_expression_conditions: newConditions });
                                        }}
                                    >
                                        <Plus size={14} />
                                        条件を追加
                                    </button>
                                </div>
                            ) : (
                                rule.regular_expression_conditions && rule.regular_expression_conditions.length > 0 && (
                                    <div className="rule-detail-list">
                                        {rule.regular_expression_conditions?.map((condition: GuidelineRegularExpressionCondition, condIndex: number) => (
                                            <div key={condIndex} className="rule-detail-condition-card">
                                                <div className="rule-detail-condition-header">
                                                    <span className="rule-detail-condition-title">条件 {condIndex + 1}</span>
                                                </div>
                                                <div className="rule-detail-condition-content">
                                                    <div className="rule-detail-field">
                                                        <label>タイプ:</label>
                                                        <span>{condition.type || 'allow_if'}</span>
                                                    </div>
                                                    {condition.context_window !== undefined && condition.context_window !== null && (
                                                        <div className="rule-detail-field">
                                                            <label>コンテキストウィンドウ:</label>
                                                            <span>{condition.context_window}</span>
                                                        </div>
                                                    )}
                                                    {condition.rule && (
                                                        <div className="rule-detail-field">
                                                            <label>ルール:</label>
                                                            <div className="rule-detail-text">{condition.rule}</div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        ))}
                                    </div>
                                )
                            )}
                        </div>
                    )}
                    {shouldShowField('musthave_not_detect_message') && (
                        <div className="rule-detail-field">
                            <label>未検出メッセージ: <span className="required">*</span></label>
                            {isEditing ? (
                                <>
                                    <input
                                        type="text"
                                        className="rule-detail-input"
                                        value={rule.musthave_not_detect_message || ''}
                                        onChange={(e) => onUpdateRule({ ...rule, musthave_not_detect_message: e.target.value })}
                                        placeholder="例: 違反箇所: PR表記が含まれていないです。 修正指示:PR表記を含めてください。"
                                    />
                                    <span className="rule-detail-helper-text">必須正規表現が検出されなかった際に表示されるメッセージ</span>
                                </>
                            ) : (
                                shouldShowField('musthave_not_detect_message') && rule.musthave_not_detect_message && <span>{rule.musthave_not_detect_message}</span>
                            )}
                        </div>
                    )}
                    {shouldShowField('forbidden_detect_message') && (
                        <div className="rule-detail-field">
                            <label>検出メッセージ: <span className="required">*</span></label>
                            {isEditing ? (
                                <>
                                    <input
                                        type="text"
                                        className="rule-detail-input"
                                        value={rule.forbidden_detect_message || ''}
                                        onChange={(e) => onUpdateRule({ ...rule, forbidden_detect_message: e.target.value })}
                                        placeholder="例: 違反箇所: {detected_text} 修正指示:正しい記載に置き換えてください。"
                                    />
                                    <span className="rule-detail-helper-text">正規表現が検出された際に表示されるメッセージ。{'{detected_text}'} を使用して検出されたテキストを挿入できます。</span>
                                </>
                            ) : (
                                shouldShowField('forbidden_detect_message') && rule.forbidden_detect_message && <span>{rule.forbidden_detect_message}</span>
                            )}
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

// Violation Category Form Component
// Each violation category can have multiple rules
interface GuidelineItemFormProps {
    item: GuidelineItem;
    onChange: (item: GuidelineItem) => void;
}

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const GuidelineItemForm: React.FC<GuidelineItemFormProps> = ({ item, onChange }) => {
    // Normalize item to ensure it has rules array with type property
    const normalizedItem: GuidelineItem & { rules: GuidelineRule[] } = (() => {
        if (item.rules && item.rules.length > 0) {
            // Ensure all rules have type
            return {
                ...item,
                rules: item.rules.map(rule => ({
                    ...rule,
                    type: (rule.type || item.type || 'description') as string,
                    patrol_source: rule.patrol_source || item.patrol_source,
                } as GuidelineRule))
            };
        }
        // Convert legacy format to rules array
        const hasLegacyFields = item.violation_check_prompt ||
            item.forbidden_regular_expressions ||
            item.musthave_regular_expressions ||
            item.detail;
        if (hasLegacyFields) {
            return {
                ...item,
                rules: [{
                    id: `rule_${Date.now()}`,
                    type: (item.type || 'description') as string,
                    patrol_source: item.patrol_source,
                    violation_check_prompt: item.violation_check_prompt,
                    existence_check_prompt: item.existence_check_prompt,
                    skip_existence_check: (item as any).skip_existence_check,
                    forbidden_regular_expressions: item.forbidden_regular_expressions,
                    forbidden_detect_message: item.forbidden_detect_message,
                    musthave_regular_expressions: item.musthave_regular_expressions,
                    musthave_not_detect_message: item.musthave_not_detect_message,
                    regular_expression_conditions: item.regular_expression_conditions,
                    context_window: item.context_window,
                    detail: item.detail,
                } as GuidelineRule]
            };
        }
        return { ...item, rules: [] as GuidelineRule[] };
    })();

    const updateField = (field: string, value: any) => {
        onChange({ ...normalizedItem, [field]: value } as GuidelineItem & { rules: GuidelineRule[] });
    };

    const updateRule = (ruleIndex: number, field: string, value: any) => {
        const newRules: GuidelineRule[] = [...normalizedItem.rules];
        const existingRule = newRules[ruleIndex];
        if (existingRule) {
            newRules[ruleIndex] = { ...existingRule, [field]: value } as GuidelineRule;
        }
        onChange({ ...normalizedItem, rules: newRules } as GuidelineItem & { rules: GuidelineRule[] });
    };

    const addRule = () => {
        const newRule: GuidelineRule = {
            id: `rule_${Date.now()}_${Math.random()}`,
            name: '',
            type: 'description',
            patrol_source: 'all_page',
            similarity_threshold: 0.97,
            min_good_matches: 50,
            min_score: 0.1,
            min_inlier_ratio: 0.4,
        };
        const newRules: GuidelineRule[] = [...normalizedItem.rules, newRule];
        onChange({ ...normalizedItem, rules: newRules } as GuidelineItem & { rules: GuidelineRule[] });
    };

    const removeRule = (ruleIndex: number) => {
        const newRules: GuidelineRule[] = normalizedItem.rules.filter((_: GuidelineRule, i: number) => i !== ruleIndex);
        onChange({ ...normalizedItem, rules: newRules } as GuidelineItem & { rules: GuidelineRule[] });
    };

    const updateRuleArrayItem = (ruleIndex: number, field: string, arrayIndex: number, value: any) => {
        const newRules: GuidelineRule[] = [...normalizedItem.rules];
        const existingRule = newRules[ruleIndex];
        if (existingRule) {
            const rule: GuidelineRule = { ...existingRule };
            const array = (rule[field] as string[]) || [];
            rule[field] = array.map((v: any, i: number) => i === arrayIndex ? value : v);
            newRules[ruleIndex] = rule;
        }
        onChange({ ...normalizedItem, rules: newRules } as GuidelineItem & { rules: GuidelineRule[] });
    };

    const addRuleArrayItem = (ruleIndex: number, field: string, defaultValue: any = '') => {
        const newRules: GuidelineRule[] = [...normalizedItem.rules];
        const existingRule = newRules[ruleIndex];
        if (existingRule) {
            const rule: GuidelineRule = { ...existingRule };
            const array = (rule[field] as string[]) || [];
            rule[field] = [...array, defaultValue];
            newRules[ruleIndex] = rule;
        }
        onChange({ ...normalizedItem, rules: newRules } as GuidelineItem & { rules: GuidelineRule[] });
    };

    const removeRuleArrayItem = (ruleIndex: number, field: string, arrayIndex: number) => {
        const newRules: GuidelineRule[] = [...normalizedItem.rules];
        const existingRule = newRules[ruleIndex];
        if (existingRule) {
            const rule: GuidelineRule = { ...existingRule };
            const array = (rule[field] as string[]) || [];
            rule[field] = array.filter((_: any, i: number) => i !== arrayIndex);
            newRules[ruleIndex] = rule;
        }
        onChange({ ...normalizedItem, rules: newRules } as GuidelineItem & { rules: GuidelineRule[] });
    };

    return (
        <div className="item-form">
            <div className="form-group">
                <label>ID <span className="required">*</span></label>
                <input
                    type="text"
                    className="form-input"
                    value={item.id || ''}
                    onChange={(e) => updateField('id', e.target.value)}
                    placeholder="unique_identifier"
                />
            </div>

            <div className="form-group">
                <label>カテゴリ名 <span className="required">*</span></label>
                <input
                    type="text"
                    className="form-input"
                    value={item.violation_category || ''}
                    onChange={(e) => updateField('violation_category', e.target.value)}
                    placeholder="違反カテゴリの名前"
                />
            </div>

            {/* Rules Section */}
            <div className="rules-section">
                <div className="rules-header">
                    <h4>ルール</h4>
                    <button
                        className="btn btn-secondary btn-sm"
                        onClick={addRule}
                    >
                        <Plus size={14} />
                        ルールを追加
                    </button>
                </div>

                {normalizedItem.rules && normalizedItem.rules.length > 0 ? (
                    <div className="rules-list">
                        {normalizedItem.rules.map((rule: GuidelineRule, ruleIndex: number) => (
                            <div key={rule.id || ruleIndex} className="rule-card">
                                <div className="rule-card-header">
                                    <h5>ルール {ruleIndex + 1}</h5>
                                    <button
                                        className="btn-icon btn-icon-danger btn-icon-sm"
                                        onClick={() => removeRule(ruleIndex)}
                                        title="ルールを削除"
                                    >
                                        <Trash2 size={14} />
                                    </button>
                                </div>

                                {/* Rule Type Selection */}
                                <div className="form-group">
                                    <label>タイプ <span className="required">*</span></label>
                                    <select
                                        className="form-select"
                                        value={rule.type || 'description'}
                                        onChange={(e) => updateRule(ruleIndex, 'type', e.target.value)}
                                    >
                                        <option value="description">説明</option>
                                        <option value="text_check">テキストチェック</option>
                                        <option value="forbidden_regular_expression_check">禁止表現チェック</option>
                                        <option value="musthave_regular_expression_check">必須表現チェック</option>
                                        <option value="ad_existence">広告存在チェック</option>
                                        <option value="link">リンクチェック</option>
                                        <option value="asp">ASPチェック</option>
                                        <option value="image_check">画像チェック</option>
                                        <option value="unknown">未定義</option>
                                    </select>
                                </div>

                                {/* Description type rules */}
                                {rule.type === 'description' && (
                                    <div className="form-group">
                                        <label>詳細</label>
                                        <textarea
                                            className="form-textarea"
                                            value={rule.detail || ''}
                                            onChange={(e) => updateRule(ruleIndex, 'detail', e.target.value)}
                                            placeholder="説明内容"
                                            rows={3}
                                        />
                                    </div>
                                )}

                                {/* Text check rules */}
                                {rule.type === 'text_check' && (
                                    <>
                                        <div className="form-group">
                                            <label>パトロールソース <span className="required">*</span></label>
                                            <select
                                                className="form-select"
                                                value={rule.patrol_source || 'all_page'}
                                                onChange={(e) => updateRule(ruleIndex, 'patrol_source', e.target.value)}
                                            >
                                                <option value="all_page">ページ全体</option>
                                                <option value="page_chunk">ページのチャンク単位</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>違反チェックプロンプト <span className="required">*</span></label>
                                            <textarea
                                                className="form-textarea"
                                                value={rule.violation_check_prompt || ''}
                                                onChange={(e) => updateRule(ruleIndex, 'violation_check_prompt', e.target.value)}
                                                placeholder="違反チェック用のプロンプト"
                                                rows={6}
                                            />
                                        </div>
                                        <div className="form-group">
                                            <label>存在チェックプロンプト <span className="required">*</span></label>
                                            <textarea
                                                className="form-textarea"
                                                value={rule.existence_check_prompt || ''}
                                                onChange={(e) => updateRule(ruleIndex, 'existence_check_prompt', e.target.value)}
                                                placeholder="存在チェック用のプロンプト"
                                                rows={6}
                                            />
                                        </div>
                                    </>
                                )}

                                {/* Forbidden regex rules */}
                                {rule.type === 'forbidden_regular_expression_check' && (
                                    <>
                                        <div className="form-group">
                                            <label>パトロールソース <span className="required">*</span></label>
                                            <select
                                                className="form-select"
                                                value={rule.patrol_source || 'all_page'}
                                                onChange={(e) => updateRule(ruleIndex, 'patrol_source', e.target.value)}
                                            >
                                                <option value="all_page">ページ全体</option>
                                                <option value="page_chunk">ページのチャンク単位</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>禁止正規表現 <span className="required">*</span></label>
                                            {(rule.forbidden_regular_expressions || []).map((expr: string, exprIndex: number) => (
                                                <div key={exprIndex} className="array-item">
                                                    <input
                                                        type="text"
                                                        className="form-input"
                                                        value={expr}
                                                        onChange={(e) => updateRuleArrayItem(ruleIndex, 'forbidden_regular_expressions', exprIndex, e.target.value)}
                                                        placeholder="正規表現パターン"
                                                    />
                                                    <button
                                                        className="btn-icon btn-icon-danger"
                                                        onClick={() => removeRuleArrayItem(ruleIndex, 'forbidden_regular_expressions', exprIndex)}
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ))}
                                            <button
                                                className="btn btn-secondary btn-sm"
                                                onClick={() => addRuleArrayItem(ruleIndex, 'forbidden_regular_expressions', '')}
                                            >
                                                <Plus size={14} />
                                                追加
                                            </button>
                                        </div>
                                        <div className="form-group">
                                            <label>検出メッセージ</label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                value={rule.forbidden_detect_message || ''}
                                                onChange={(e) => updateRule(ruleIndex, 'forbidden_detect_message', e.target.value)}
                                                placeholder="違反箇所: {detected_text} 修正指示:..."
                                            />
                                        </div>
                                    </>
                                )}

                                {/* Must-have regex rules */}
                                {rule.type === 'musthave_regular_expression_check' && (
                                    <>
                                        <div className="form-group">
                                            <label>パトロールソース <span className="required">*</span></label>
                                            <select
                                                className="form-select"
                                                value={rule.patrol_source || 'all_page'}
                                                onChange={(e) => updateRule(ruleIndex, 'patrol_source', e.target.value)}
                                            >
                                                <option value="all_page">ページ全体</option>
                                                <option value="page_chunk">ページのチャンク単位</option>
                                            </select>
                                        </div>
                                        <div className="form-group">
                                            <label>必須正規表現 <span className="required">*</span></label>
                                            {(rule.musthave_regular_expressions || []).map((expr: string, exprIndex: number) => (
                                                <div key={exprIndex} className="array-item">
                                                    <input
                                                        type="text"
                                                        className="form-input"
                                                        value={expr}
                                                        onChange={(e) => updateRuleArrayItem(ruleIndex, 'musthave_regular_expressions', exprIndex, e.target.value)}
                                                        placeholder="正規表現パターン"
                                                    />
                                                    <button
                                                        className="btn-icon btn-icon-danger"
                                                        onClick={() => removeRuleArrayItem(ruleIndex, 'musthave_regular_expressions', exprIndex)}
                                                    >
                                                        <X size={16} />
                                                    </button>
                                                </div>
                                            ))}
                                            <button
                                                className="btn btn-secondary btn-sm"
                                                onClick={() => addRuleArrayItem(ruleIndex, 'musthave_regular_expressions', '')}
                                            >
                                                <Plus size={14} />
                                                追加
                                            </button>
                                        </div>
                                        <div className="form-group">
                                            <label>未検出メッセージ</label>
                                            <input
                                                type="text"
                                                className="form-input"
                                                value={rule.musthave_not_detect_message || ''}
                                                onChange={(e) => updateRule(ruleIndex, 'musthave_not_detect_message', e.target.value)}
                                                placeholder="違反箇所: ... 修正指示:..."
                                            />
                                        </div>
                                    </>
                                )}

                                {/* Other types */}
                                {(rule.type === 'ad_existence' || rule.type === 'link' || rule.type === 'asp' || rule.type === 'image_check' || rule.type === 'unknown') && (
                                    <div className="form-group">
                                        <label>詳細</label>
                                        <textarea
                                            className="form-textarea"
                                            value={rule.detail || ''}
                                            onChange={(e) => updateRule(ruleIndex, 'detail', e.target.value)}
                                            placeholder="詳細説明"
                                            rows={3}
                                        />
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                ) : (
                    <div className="empty-rules">
                        <p>ルールがありません</p>
                        <button
                            className="btn btn-primary btn-sm"
                            onClick={addRule}
                        >
                            <Plus size={14} />
                            最初のルールを追加
                        </button>
                    </div>
                )}
            </div>
        </div>
    );
};

// Helper functions to generate YAML content
const indentYamlBlock = (yamlString: string, spaces: number) => {
    const indent = ' '.repeat(spaces);
    return yamlString
        .split('\n')
        .map(line => (line.trim().length === 0 ? line : `${indent}${line}`))
        .join('\n');
};

const escapeYamlString = (value: string) => value.replace(/\\/g, '\\\\').replace(/"/g, '\\"');

const formatStringListYaml = (label: string, values: string[]) => {
    if (!values || values.length === 0) {
        return `  ${label}: []`;
    }
    return [`  ${label}:`, ...values.map(value => `    - "${escapeYamlString(value)}"`)].join('\n');
};

const formatUnfixableByDomainYaml = (rules: UnfixableDomainRule[]) => {
    if (!rules || rules.length === 0) {
        return '  unfixable_by_domain: []';
    }
    const lines: string[] = ['  unfixable_by_domain:'];
    for (const rule of rules) {
        lines.push(`    - domain: "${escapeYamlString(rule.domain)}"`);
        const desc = rule.description.replace(/\n/g, '\\n');
        lines.push(`      description: "${escapeYamlString(desc)}"`);
        if (rule.asp) {
            lines.push(`      asp: "${escapeYamlString(rule.asp)}"`);
        }
        if (rule.operation_company) {
            lines.push(`      operation_company: "${escapeYamlString(rule.operation_company)}"`);
        }
        if (rule.media_name) {
            lines.push(`      media_name: "${escapeYamlString(rule.media_name)}"`);
        }
    }
    return lines.join('\n');
};

const buildMetadataYaml = (metadata: {
    relatedAsps: string[];
    operationCompanies: string[];
    mediaNames: string[];
    chunkSettings: {
        chunk_context_before: number;
        chunk_context_after: number;
        max_chunk_size: number;
        chunk_overlap: number;
        keyword_group_distance: number;
    };
    keywordList: string[];
    enableSearch: boolean;
    searchKeywords: string[];
    enableFiltering: boolean;
    titleExtractionKeywords: string[];
    autoExpandKeywords: string[];
    enableUnfixableClassification: boolean;
    unfixableByDomain: UnfixableDomainRule[];
}) => {
    const lines: string[] = ['metadata:'];
    lines.push(formatStringListYaml('related_asps', metadata.relatedAsps));
    lines.push(formatStringListYaml('operation_companies', metadata.operationCompanies));
    lines.push(formatStringListYaml('media_names', metadata.mediaNames));
    lines.push('  chunk_settings:');
    lines.push(`    chunk_context_before: ${metadata.chunkSettings.chunk_context_before}`);
    lines.push(`    chunk_context_after: ${metadata.chunkSettings.chunk_context_after}`);
    lines.push(`    max_chunk_size: ${metadata.chunkSettings.max_chunk_size}`);
    lines.push(`    chunk_overlap: ${metadata.chunkSettings.chunk_overlap}`);
    lines.push(`    keyword_group_distance: ${metadata.chunkSettings.keyword_group_distance}`);
    lines.push(formatStringListYaml('keyword_list', metadata.keywordList));
    lines.push(`  enable_search: ${metadata.enableSearch}`);
    if (metadata.enableSearch && metadata.searchKeywords.length > 0) {
        lines.push(formatStringListYaml('search_keywords', metadata.searchKeywords));
    }
    lines.push(`  enable_filtering: ${metadata.enableFiltering}`);
    if (metadata.titleExtractionKeywords && metadata.titleExtractionKeywords.length > 0) {
        lines.push(formatStringListYaml('other_content_keywords', metadata.titleExtractionKeywords));
    }
    if (metadata.autoExpandKeywords && metadata.autoExpandKeywords.length > 0) {
        lines.push(formatStringListYaml('auto_expand_keywords', metadata.autoExpandKeywords));
    }
    lines.push(`  enable_unfixable_classification: ${metadata.enableUnfixableClassification}`);
    lines.push(formatUnfixableByDomainYaml(metadata.unfixableByDomain));
    return lines.join('\n');
};

const guidelineToYAML = (
    items: GuidelineItem[],
    metadata: {
        relatedAsps: string[];
        operationCompanies: string[];
        mediaNames: string[];
        chunkSettings: {
            chunk_context_before: number;
            chunk_context_after: number;
            max_chunk_size: number;
            chunk_overlap: number;
            keyword_group_distance: number;
        };
        keywordList: string[];
        enableSearch: boolean;
        searchKeywords: string[];
        enableFiltering: boolean;
        titleExtractionKeywords: string[];
        autoExpandKeywords: string[];
        enableUnfixableClassification: boolean;
        unfixableByDomain: UnfixableDomainRule[];
    }
): string => {
    const metadataYaml = buildMetadataYaml(metadata);
    const itemsYamlContent = guidelineItemsToYAML(items).trimEnd();
    if (!itemsYamlContent.trim()) {
        return `${metadataYaml}\nitems: []\n`;
    }
    const indentedItems = indentYamlBlock(itemsYamlContent, 2);
    return `${metadataYaml}\nitems:\n${indentedItems}\n`;
};

const parseYamlUnfixableByDomain = (raw: unknown): UnfixableDomainRule[] => {
    if (!Array.isArray(raw)) return [];
    return raw
        .map((entry: any) => {
            const domain = trimPromptAndText(entry?.domain);
            const description = trimPromptAndText(entry?.description);
            if (!domain || !description) return null;
            return {
                id: trimPromptAndText(entry?.id) || crypto.randomUUID(),
                domain,
                description,
                asp: trimPromptAndText(entry?.asp) || undefined,
                operation_company:
                    trimPromptAndText(entry?.operation_company ?? entry?.operationCompany) ||
                    undefined,
                media_name:
                    trimPromptAndText(entry?.media_name ?? entry?.mediaName) || undefined,
                source_row: entry?.source_row ?? entry?.sourceRow ?? null,
            } as UnfixableDomainRule;
        })
        .filter((r): r is UnfixableDomainRule => r != null);
};

const validateMetadata = (metadata: GuidelineYamlMetadata) => {
    const cs = metadata.chunkSettings;
    if (cs) {
        const check = (name: string, v: unknown) => {
            const n = Number(v);
            if (v != null && (typeof v !== 'number' || isNaN(n) || n < 0)) {
                throw new Error(`metadata.chunk_settings.${name} は0以上の数値で指定してください。`);
            }
        };
        check('chunk_context_before', cs.chunk_context_before);
        check('chunk_context_after', cs.chunk_context_after);
        check('max_chunk_size', cs.max_chunk_size);
        check('chunk_overlap', cs.chunk_overlap);
        check('keyword_group_distance', cs.keyword_group_distance);
    }
    if (metadata.enableSearch != null && typeof metadata.enableSearch !== 'boolean') {
        throw new Error('metadata.enable_search は true または false で指定してください。');
    }
    if (metadata.enableFiltering != null && typeof metadata.enableFiltering !== 'boolean') {
        throw new Error('metadata.enable_filtering は true または false で指定してください。');
    }
    if (metadata.enableSearch && (!metadata.searchKeywords || metadata.searchKeywords.length === 0)) {
        throw new Error('metadata.enable_search が true の場合は search_keywords を1つ以上指定してください。');
    }
    if (
        metadata.enableUnfixableClassification != null &&
        typeof metadata.enableUnfixableClassification !== 'boolean'
    ) {
        throw new Error(
            'metadata.enable_unfixable_classification は true または false で指定してください。'
        );
    }
};

const parseGuidelineYaml = (yamlString: string): { items: GuidelineItem[]; metadata?: GuidelineYamlMetadata } => {
    try {
        if (!yamlString || yamlString.trim() === '') {
            return { items: [] };
        }

        const parsed = yaml.load(yamlString);

        if (parsed === null || parsed === undefined) {
            return { items: [] };
        }

        if (Array.isArray(parsed)) {
            return { items: convertRawYamlItemsToGuidelineItems(parsed) };
        }

        if (typeof parsed === 'object') {
            const metadataSection = (parsed as any).metadata;
            const itemsSection = Array.isArray((parsed as any).items) ? (parsed as any).items : [];
            const trimStringArray = (arr: any): string[] =>
                Array.isArray(arr) ? arr.map((s: any) => trimPromptAndText(s)).filter((s: string) => s !== '') : [];
            const metadata: GuidelineYamlMetadata = {
                relatedAsps: trimStringArray(metadataSection?.related_asps ?? metadataSection?.relatedAsps),
                operationCompanies: trimStringArray(metadataSection?.operation_companies ?? metadataSection?.operationCompanies),
                mediaNames: trimStringArray(metadataSection?.media_names ?? metadataSection?.mediaNames),
                chunkSettings: {
                    chunk_context_before: metadataSection?.chunk_settings?.chunk_context_before,
                    chunk_context_after: metadataSection?.chunk_settings?.chunk_context_after,
                    max_chunk_size: metadataSection?.chunk_settings?.max_chunk_size,
                    chunk_overlap: metadataSection?.chunk_settings?.chunk_overlap,
                    keyword_group_distance: metadataSection?.chunk_settings?.keyword_group_distance,
                },
                keywordList: trimStringArray(metadataSection?.keyword_list ?? metadataSection?.keywordList),
                enableSearch: metadataSection?.enable_search ?? metadataSection?.enableSearch ?? false,
                searchKeywords: trimStringArray(metadataSection?.search_keywords ?? metadataSection?.searchKeywords),
                enableFiltering: metadataSection?.enable_filtering ?? metadataSection?.enableFiltering ?? true,
                titleExtractionKeywords: trimStringArray(
                    metadataSection?.other_content_keywords ?? metadataSection?.titleExtractionKeywords
                ),
                autoExpandKeywords: trimStringArray(
                    metadataSection?.auto_expand_keywords ?? metadataSection?.autoExpandKeywords
                ),
                enableUnfixableClassification:
                    metadataSection?.enable_unfixable_classification ??
                    metadataSection?.enableUnfixableClassification ??
                    false,
                unfixableByDomain: parseYamlUnfixableByDomain(
                    metadataSection?.unfixable_by_domain ?? metadataSection?.unfixableByDomain
                ),
            };

            validateMetadata(metadata);

            return {
                items: convertRawYamlItemsToGuidelineItems(itemsSection),
                metadata,
            };
        }

        throw new Error('YAML must contain an items list');
    } catch (err: any) {
        throw new Error(`YAML解析エラー: ${err.message}`);
    }
};

const VALID_RULE_TYPES = new Set([
    'text_check',
    'forbidden_regular_expression_check',
    'musthave_regular_expression_check',
    'forbidden_image',
    'required_image_match',
    // Kept for backward compatibility in YAML parsing (we infer to one of the above when possible)
    'description',
]);

const VALID_PATROL_SOURCES = new Set(['all_page', 'page_chunk']);

const formatRulePath = (itemIndex: number, ruleIndex: number) => `items[${itemIndex}].rules[${ruleIndex}]`;

const validateItemShape = (item: any, index: number) => {
    const itemPath = `items[${index}]`;
    const id = trimPromptAndText(item.id);
    const violationCategory = trimPromptAndText(item.violation_category || item.title);
    if (!id) throw new Error(`${itemPath}.id を指定してください。（空は不可）`);
    if (!violationCategory) throw new Error(`${itemPath}.violation_category を指定してください。（空は不可）`);
    if (item.rules != null && !Array.isArray(item.rules)) throw new Error(`${itemPath}.rules は配列で指定してください。`);
};

const validateRuleShape = (rule: GuidelineRule, itemIndex: number, ruleIndex: number) => {
    const rulePath = formatRulePath(itemIndex, ruleIndex);
    const type = trimPromptAndText(rule.type);
    const patrolSource = trimPromptAndText(rule.patrol_source);

    if (!trimPromptAndText(rule.name)) {
        throw new Error(`${rulePath}.name を指定してください。（空は不可）`);
    }

    // Common authoring mistake: using patrol_source value in `type`.
    if (type === 'page_chunk' || type === 'all_page') {
        throw new Error(
            `${rulePath}.type が不正です: "${type}"。\n` +
            `type には "text_check" / "forbidden_regular_expression_check" / "musthave_regular_expression_check" / "forbidden_image" / "required_image_match" のいずれかを指定してください。`
        );
    }

    if (type && !VALID_RULE_TYPES.has(type)) {
        throw new Error(
            `${rulePath}.type が未対応です: "${type}"。\n` +
            `対応している type: ${Array.from(VALID_RULE_TYPES).filter(t => t !== 'description').map(t => `"${t}"`).join(' / ')}`
        );
    }

    if (patrolSource && !VALID_PATROL_SOURCES.has(patrolSource)) {
        throw new Error(
            `${rulePath}.patrol_source が不正です: "${patrolSource}"。\n` +
            `patrol_source は "all_page" または "page_chunk" を指定してください。`
        );
    }

    const effectiveType = type || 'description';
    if (effectiveType === 'text_check') {
        if (!patrolSource) {
            throw new Error(`${rulePath}.patrol_source を指定してください。（text_check では必須です）`);
        }
        if (!trimPromptAndText(rule.violation_check_prompt)) {
            throw new Error(`${rulePath}.violation_check_prompt を指定してください。（text_check では必須です）`);
        }
        if (!rule.skip_existence_check && !trimPromptAndText(rule.existence_check_prompt)) {
            throw new Error(`${rulePath}.existence_check_prompt を指定してください。（text_check では必須です。存在チェックをスキップする場合は skip_existence_check: true を指定してください）`);
        }
    }
    if (effectiveType === 'forbidden_regular_expression_check') {
        if (!patrolSource) {
            throw new Error(`${rulePath}.patrol_source を指定してください。（forbidden_regular_expression_check では必須です）`);
        }
        if (!rule.forbidden_regular_expressions || !Array.isArray(rule.forbidden_regular_expressions) || rule.forbidden_regular_expressions.length === 0) {
            throw new Error(`${rulePath}.forbidden_regular_expressions を1つ以上指定してください。`);
        }
    }
    if (effectiveType === 'musthave_regular_expression_check') {
        if (!patrolSource) {
            throw new Error(`${rulePath}.patrol_source を指定してください。（musthave_regular_expression_check では必須です）`);
        }
        if (!rule.musthave_regular_expressions || !Array.isArray(rule.musthave_regular_expressions) || rule.musthave_regular_expressions.length === 0) {
            throw new Error(`${rulePath}.musthave_regular_expressions を1つ以上指定してください。`);
        }
    }
    if (effectiveType === 'forbidden_image') {
        const assets = (rule as any).forbidden_image_assets;
        if (!Array.isArray(assets) || assets.length === 0) {
            throw new Error(`${rulePath}.forbidden_image_assets を1つ以上指定してください。`);
        }
        assets.forEach((asset: any, idx: number) => {
            if (!asset || typeof asset !== 'object') {
                throw new Error(`${rulePath}.forbidden_image_assets[${idx}] はオブジェクトで指定してください。`);
            }
            const aid = trimPromptAndText(asset.id);
            const fp = trimPromptAndText(asset.file_path);
            const ct = trimPromptAndText(asset.content_type);
            if (!aid) throw new Error(`${rulePath}.forbidden_image_assets[${idx}].id を指定してください。`);
            if (!fp) throw new Error(`${rulePath}.forbidden_image_assets[${idx}].file_path を指定してください。`);
            if (!ct) throw new Error(`${rulePath}.forbidden_image_assets[${idx}].content_type を指定してください。`);
        });
    }
    if (effectiveType === 'required_image_match') {
        const prompt = trimPromptAndText((rule as any).target_image_extraction_prompt);
        if (!prompt) {
            throw new Error(`${rulePath}.target_image_extraction_prompt を指定してください。（required_image_match では必須です）`);
        }
        const assets = (rule as any).required_image_assets;
        if (!Array.isArray(assets)) {
            throw new Error(`${rulePath}.required_image_assets は配列で指定してください。`);
        }
        assets.forEach((asset: any, idx: number) => {
            if (!asset || typeof asset !== 'object') {
                throw new Error(`${rulePath}.required_image_assets[${idx}] はオブジェクトで指定してください。`);
            }
            const aid = trimPromptAndText(asset.id);
            const fp = trimPromptAndText(asset.file_path);
            const ct = trimPromptAndText(asset.content_type);
            if (!aid) throw new Error(`${rulePath}.required_image_assets[${idx}].id を指定してください。`);
            if (!fp) throw new Error(`${rulePath}.required_image_assets[${idx}].file_path を指定してください。`);
            if (!ct) throw new Error(`${rulePath}.required_image_assets[${idx}].content_type を指定してください。`);
        });
    }
    if (rule.regular_expression_conditions != null) {
        if (!Array.isArray(rule.regular_expression_conditions)) {
            throw new Error(`${rulePath}.regular_expression_conditions は配列で指定してください。`);
        }
        rule.regular_expression_conditions.forEach((cond: any, idx: number) => {
            if (!cond || typeof cond !== 'object') {
                throw new Error(`${rulePath}.regular_expression_conditions[${idx}] はオブジェクトで指定してください。`);
            }
            if (!trimPromptAndText(cond.rule)) {
                throw new Error(`${rulePath}.regular_expression_conditions[${idx}].rule を指定してください。`);
            }
        });
    }
};

const yamlDiagnostics = (yamlText: string): Diagnostic[] => {
    if (!yamlText) return [];

    const diagnostics: Diagnostic[] = [];
    const lineStarts = computeLineStarts(yamlText);

    // 1) Syntax/parse errors (js-yaml provides line/column)
    try {
        yaml.load(yamlText);
    } catch (err: any) {
        const mark = err?.mark;
        const line = typeof mark?.line === 'number' ? mark.line : 0;
        const col = typeof mark?.column === 'number' ? mark.column : 0;
        const from = typeof mark?.position === 'number'
            ? Math.max(0, mark.position)
            : lineColToPos(lineStarts, line, col);
        diagnostics.push({
            from,
            to: Math.min(from + 1, yamlText.length),
            severity: 'error',
            message: err?.message || 'YAML parse error',
        });
        return diagnostics;
    }

    // 2) Highlight invalid type values (any type not in valid list) directly in the editor
    const invalidTypeRanges = findInvalidTypeLineRanges(yamlText);
    const validTypeList =
        '"text_check" / "forbidden_regular_expression_check" / "musthave_regular_expression_check" / "forbidden_image" / "required_image_match"';
    for (const { from, to, value } of invalidTypeRanges) {
        const message =
            value === 'page_chunk' || value === 'all_page'
                ? `type が不正です: "${value}"。type には ${validTypeList} のいずれかを指定してください。`
                : `type が未対応です: "${value}"。対応している type: ${validTypeList}`;
        diagnostics.push({
            from,
            to,
            severity: 'error',
            message,
        });
    }

    // 2b) Highlight invalid patrol_source values (must be all_page or page_chunk)
    const invalidPatrolRanges = findInvalidPatrolSourceLineRanges(yamlText);
    for (const { from, to, value } of invalidPatrolRanges) {
        diagnostics.push({
            from,
            to,
            severity: 'error',
            message: `patrol_source が不正です: "${value}"。patrol_source は "all_page" または "page_chunk" を指定してください。`,
        });
    }

    // 2c) Highlight empty required item fields (id, violation_category)
    const emptyFieldRanges = findEmptyRequiredFieldLineRanges(yamlText);
    for (const { from, to, field } of emptyFieldRanges) {
        const msg = field === 'id'
            ? 'id を指定してください。（空は不可）'
            : 'violation_category を指定してください。（空は不可）';
        diagnostics.push({ from, to, severity: 'error', message: msg });
    }

    // 2d) type: forbidden_regular_expression_check but no forbidden_regular_expressions in same block
    const missingForbiddenRanges = findMissingForbiddenRegexLineRanges(yamlText);
    for (const { from, to } of missingForbiddenRanges) {
        diagnostics.push({
            from,
            to,
            severity: 'error',
            message: 'forbidden_regular_expression_check では forbidden_regular_expressions を1つ以上指定してください。',
        });
    }

    // 2e) type: musthave_regular_expression_check but no musthave_regular_expressions in same block
    const missingMusthaveRanges = findMissingMusthaveRegexLineRanges(yamlText);
    for (const { from, to } of missingMusthaveRanges) {
        diagnostics.push({
            from,
            to,
            severity: 'error',
            message: 'musthave_regular_expression_check では musthave_regular_expressions を1つ以上指定してください。',
        });
    }

    // 2f) type: text_check but missing violation_check_prompt or existence_check_prompt in same block
    const missingTextCheckRanges = findMissingTextCheckPromptsLineRanges(yamlText);
    for (const { from, to, message } of missingTextCheckRanges) {
        diagnostics.push({ from, to, severity: 'error', message });
    }

    // 3) If no invalid-type/invalid-patrol lines found but full validation still fails, add one diagnostic with full message
    if (diagnostics.length === 0) {
        try {
            parseGuidelineYaml(yamlText);
        } catch (err: any) {
            const msg = String(err?.message || 'YAML validation error');
            const typeRanges = findInvalidTypeLineRanges(yamlText);
            const patrolRanges = findInvalidPatrolSourceLineRanges(yamlText);
            const emptyRanges = findEmptyRequiredFieldLineRanges(yamlText);
            const firstRange = typeRanges[0] ?? patrolRanges[0] ?? (emptyRanges[0] ? { from: emptyRanges[0].from, to: emptyRanges[0].to } : null);
            if (firstRange) {
                diagnostics.push({
                    from: firstRange.from,
                    to: firstRange.to,
                    severity: 'error',
                    message: msg,
                });
            } else {
                diagnostics.push({
                    from: 0,
                    to: Math.min(1, yamlText.length),
                    severity: 'error',
                    message: msg,
                });
            }
        }
    }

    return diagnostics;
};

const convertRawYamlItemsToGuidelineItems = (parsedItems: any[]): GuidelineItem[] => {
    return parsedItems.map((item: any, index: number) => {
        validateItemShape(item, index);
        const guidelineItem: GuidelineItem = {
            id: trimPromptAndText(item.id) || `item_${index}`,
            violation_category: trimPromptAndText(item.violation_category || item.title) || '',
            rules: []
        };

        if (item.rules && Array.isArray(item.rules)) {
            guidelineItem.rules = item.rules.map((rule: any, ruleIndex: number) => {
                const forbiddenAssetsRaw = Array.isArray(rule.forbidden_image_assets) ? rule.forbidden_image_assets : [];
                const forbidden_image_assets =
                    forbiddenAssetsRaw.length > 0
                        ? forbiddenAssetsRaw
                            .map((asset: any) => {
                                if (!asset || typeof asset !== 'object') return null;
                                return {
                                    ...asset,
                                    id: trimPromptAndText(asset.id),
                                    file_path: trimPromptAndText(asset.file_path),
                                    content_type: trimPromptAndText(asset.content_type),
                                    original_filename: trimPromptAndText(asset.original_filename) || undefined,
                                    created_at: trimPromptAndText(asset.created_at) || undefined,
                                    created_by: trimPromptAndText(asset.created_by) || undefined,
                                    width: asset.width,
                                    height: asset.height,
                                };
                            })
                            .filter((a: any) => a && trimPromptAndText(a.id) && trimPromptAndText(a.file_path) && trimPromptAndText(a.content_type))
                        : undefined;
                const requiredAssetsRaw = Array.isArray(rule.required_image_assets) ? rule.required_image_assets : [];
                const required_image_assets =
                    requiredAssetsRaw.length > 0
                        ? requiredAssetsRaw
                            .map((asset: any) => {
                                if (!asset || typeof asset !== 'object') return null;
                                return {
                                    ...asset,
                                    id: trimPromptAndText(asset.id),
                                    file_path: trimPromptAndText(asset.file_path),
                                    content_type: trimPromptAndText(asset.content_type),
                                    original_filename: trimPromptAndText(asset.original_filename) || undefined,
                                    created_at: trimPromptAndText(asset.created_at) || undefined,
                                    created_by: trimPromptAndText(asset.created_by) || undefined,
                                    width: asset.width,
                                    height: asset.height,
                                };
                            })
                            .filter((a: any) => a && trimPromptAndText(a.id) && trimPromptAndText(a.file_path) && trimPromptAndText(a.content_type))
                        : undefined;

                const guidelineRule: GuidelineRule = {
                    id: trimPromptAndText(rule.id) || `rule_${ruleIndex + 1}`,
                    name: trimPromptAndText(rule.name) || `Rule ${ruleIndex + 1}`,
                    type: trimPromptAndText(rule.type) || 'description',
                    patrol_source: trimPromptAndText(rule.patrol_source) || undefined,
                    detail: trimPromptAndText(rule.detail) || undefined,
                    violation_check_prompt: trimPromptAndText(rule.violation_check_prompt) || undefined,
                    existence_check_prompt: trimPromptAndText(rule.existence_check_prompt) || undefined,
                    skip_existence_check: Boolean(rule.skip_existence_check) || undefined,
                    forbidden_regular_expressions: Array.isArray(rule.forbidden_regular_expressions)
                        ? rule.forbidden_regular_expressions.map((s: any) => trimPromptAndText(s)).filter((s: string) => s !== '')
                        : undefined,
                    forbidden_detect_message: trimPromptAndText(rule.forbidden_detect_message) || undefined,
                    musthave_regular_expressions: Array.isArray(rule.musthave_regular_expressions)
                        ? rule.musthave_regular_expressions.map((s: any) => trimPromptAndText(s)).filter((s: string) => s !== '')
                        : undefined,
                    musthave_not_detect_message: trimPromptAndText(rule.musthave_not_detect_message) || undefined,
                    regular_expression_conditions: Array.isArray(rule.regular_expression_conditions)
                        ? rule.regular_expression_conditions.map((cond: any) =>
                            cond && typeof cond === 'object'
                                ? { ...cond, rule: trimPromptAndText(cond.rule) }
                                : cond
                        ).filter((c: any) => c && trimPromptAndText(c.rule) !== '')
                        : undefined,
                    context_window: rule.context_window,
                    forbidden_image_assets: forbidden_image_assets as any,
                    required_image_assets: required_image_assets as any,
                    target_image_extraction_prompt: trimPromptAndText(rule.target_image_extraction_prompt) || undefined,
                    similarity_threshold: parseOptionalNumber(rule.similarity_threshold),
                    min_good_matches: parseOptionalInt(rule.min_good_matches),
                    min_score: parseOptionalNumber(rule.min_score),
                    min_inlier_ratio: parseOptionalNumber(rule.min_inlier_ratio),
                };
                validateRuleShape(guidelineRule, index, ruleIndex);
                // If type is unspecified/description, infer from presence of fields.
                if (!guidelineRule.type || guidelineRule.type === 'description') {
                    if (guidelineRule.violation_check_prompt || guidelineRule.existence_check_prompt) {
                        guidelineRule.type = 'text_check';
                    } else if (guidelineRule.forbidden_regular_expressions && guidelineRule.forbidden_regular_expressions.length > 0) {
                        guidelineRule.type = 'forbidden_regular_expression_check';
                    } else if (guidelineRule.musthave_regular_expressions && guidelineRule.musthave_regular_expressions.length > 0) {
                        guidelineRule.type = 'musthave_regular_expression_check';
                    } else if (guidelineRule.forbidden_image_assets && guidelineRule.forbidden_image_assets.length > 0) {
                        guidelineRule.type = 'forbidden_image';
                    } else if (guidelineRule.required_image_assets && guidelineRule.required_image_assets.length > 0) {
                        guidelineRule.type = 'required_image_match';
                    }
                }
                validateRuleShape(guidelineRule, index, ruleIndex);
                return guidelineRule;
            });
        } else {
            const forbiddenAssetsRaw = Array.isArray(item.forbidden_image_assets) ? item.forbidden_image_assets : [];
            const forbidden_image_assets =
                forbiddenAssetsRaw.length > 0
                    ? forbiddenAssetsRaw
                        .map((asset: any) => {
                            if (!asset || typeof asset !== 'object') return null;
                            return {
                                ...asset,
                                id: trimPromptAndText(asset.id),
                                file_path: trimPromptAndText(asset.file_path),
                                content_type: trimPromptAndText(asset.content_type),
                                original_filename: trimPromptAndText(asset.original_filename) || undefined,
                                created_at: trimPromptAndText(asset.created_at) || undefined,
                                created_by: trimPromptAndText(asset.created_by) || undefined,
                                width: asset.width,
                                height: asset.height,
                            };
                        })
                        .filter((a: any) => a && trimPromptAndText(a.id) && trimPromptAndText(a.file_path) && trimPromptAndText(a.content_type))
                    : undefined;
            const requiredAssetsRaw = Array.isArray(item.required_image_assets) ? item.required_image_assets : [];
            const required_image_assets =
                requiredAssetsRaw.length > 0
                    ? requiredAssetsRaw
                        .map((asset: any) => {
                            if (!asset || typeof asset !== 'object') return null;
                            return {
                                ...asset,
                                id: trimPromptAndText(asset.id),
                                file_path: trimPromptAndText(asset.file_path),
                                content_type: trimPromptAndText(asset.content_type),
                                original_filename: trimPromptAndText(asset.original_filename) || undefined,
                                created_at: trimPromptAndText(asset.created_at) || undefined,
                                created_by: trimPromptAndText(asset.created_by) || undefined,
                                width: asset.width,
                                height: asset.height,
                            };
                        })
                        .filter((a: any) => a && trimPromptAndText(a.id) && trimPromptAndText(a.file_path) && trimPromptAndText(a.content_type))
                    : undefined;

            const rule: GuidelineRule = {
                id: 'rule_1',
                name: 'Rule 1',
                type: trimPromptAndText(item.type) || 'description',
                patrol_source: trimPromptAndText(item.patrol_source) || undefined,
                detail: trimPromptAndText(item.detail) || undefined,
                violation_check_prompt: trimPromptAndText(item.violation_check_prompt) || undefined,
                existence_check_prompt: trimPromptAndText(item.existence_check_prompt) || undefined,
                skip_existence_check: Boolean(item.skip_existence_check) || undefined,
                forbidden_regular_expressions: Array.isArray(item.forbidden_regular_expressions)
                    ? item.forbidden_regular_expressions.map((s: any) => trimPromptAndText(s)).filter((s: string) => s !== '')
                    : undefined,
                forbidden_detect_message: trimPromptAndText(item.forbidden_detect_message) || undefined,
                musthave_regular_expressions: Array.isArray(item.musthave_regular_expressions)
                    ? item.musthave_regular_expressions.map((s: any) => trimPromptAndText(s)).filter((s: string) => s !== '')
                    : undefined,
                musthave_not_detect_message: trimPromptAndText(item.musthave_not_detect_message) || undefined,
                regular_expression_conditions: Array.isArray(item.regular_expression_conditions)
                    ? item.regular_expression_conditions.map((cond: any) =>
                        cond && typeof cond === 'object'
                            ? { ...cond, rule: trimPromptAndText(cond.rule) }
                            : cond
                    ).filter((c: any) => c && trimPromptAndText(c.rule) !== '')
                    : undefined,
                context_window: item.context_window,
                forbidden_image_assets: forbidden_image_assets as any,
                required_image_assets: required_image_assets as any,
                target_image_extraction_prompt: trimPromptAndText(item.target_image_extraction_prompt) || undefined,
                similarity_threshold: parseOptionalNumber(item.similarity_threshold),
                min_good_matches: parseOptionalInt(item.min_good_matches),
                min_score: parseOptionalNumber(item.min_score),
                min_inlier_ratio: parseOptionalNumber(item.min_inlier_ratio),
            };

            validateRuleShape(rule, index, 0);
            if (!rule.type || rule.type === 'description') {
                if (rule.violation_check_prompt || rule.existence_check_prompt) {
                    rule.type = 'text_check';
                } else if (rule.forbidden_regular_expressions && rule.forbidden_regular_expressions.length > 0) {
                    rule.type = 'forbidden_regular_expression_check';
                } else if (rule.musthave_regular_expressions && rule.musthave_regular_expressions.length > 0) {
                    rule.type = 'musthave_regular_expression_check';
                } else if (rule.forbidden_image_assets && rule.forbidden_image_assets.length > 0) {
                    rule.type = 'forbidden_image';
                } else if (rule.required_image_assets && rule.required_image_assets.length > 0) {
                    rule.type = 'required_image_match';
                }
            }
            validateRuleShape(rule, index, 0);

            guidelineItem.rules = [rule];
        }

        return guidelineItem;
    });
};

const quoteYamlString = (value: string) => `"${value.replace(/\\/g, '\\\\').replace(/"/g, '\\"')}"`;

const appendYamlScalar = (lines: string[], indent: number, label: string, value?: string | null) => {
    if (value === undefined || value === null) {
        return;
    }
    const s = String(value);
    if (s.trim() === '') {
        return;
    }

    const pad = ' '.repeat(indent);
    if (s.includes('\n')) {
        lines.push(`${pad}${label}: |`);
        s.split('\n').forEach(line => {
            lines.push(`${pad}  ${line}`);
        });
    } else {
        lines.push(`${pad}${label}: ${quoteYamlString(s)}`);
    }
};

const appendYamlNumberIfPresent = (lines: string[], indent: number, label: string, value?: number | null) => {
    if (value === undefined || value === null) return;
    if (typeof value !== 'number' || Number.isNaN(value)) return;
    const pad = ' '.repeat(indent);
    lines.push(`${pad}${label}: ${value}`);
};

const appendYamlImageAssets = (lines: string[], indent: number, label: string, assets?: any[] | null) => {
    const pad = ' '.repeat(indent);
    if (!assets || assets.length === 0) {
        lines.push(`${pad}${label}: []`);
        return;
    }
    lines.push(`${pad}${label}:`);
    assets.forEach((asset) => {
        lines.push(`${pad}  -`);
        appendYamlScalar(lines, indent + 4, 'id', trimPromptAndText(asset?.id));
        appendYamlScalar(lines, indent + 4, 'file_path', trimPromptAndText(asset?.file_path));
        appendYamlScalar(lines, indent + 4, 'content_type', trimPromptAndText(asset?.content_type));
        appendYamlScalar(lines, indent + 4, 'original_filename', trimPromptAndText(asset?.original_filename));
        appendYamlScalar(lines, indent + 4, 'created_at', trimPromptAndText(asset?.created_at));
        appendYamlScalar(lines, indent + 4, 'created_by', trimPromptAndText(asset?.created_by));
        if (asset?.width !== undefined && asset?.width !== null) {
            lines.push(`${' '.repeat(indent + 4)}width: ${asset.width}`);
        }
        if (asset?.height !== undefined && asset?.height !== null) {
            lines.push(`${' '.repeat(indent + 4)}height: ${asset.height}`);
        }
    });
};

const appendYamlStringArray = (lines: string[], indent: number, label: string, values?: string[] | null) => {
    const pad = ' '.repeat(indent);
    if (!values || values.length === 0) {
        lines.push(`${pad}${label}: []`);
        return;
    }
    lines.push(`${pad}${label}:`);
    values.forEach(value => {
        lines.push(`${pad}  - ${quoteYamlString(value)}`);
    });
};

const guidelineItemsToYAML = (items: GuidelineItem[]): string => {
    const lines: string[] = [];
    items.forEach(item => {
        lines.push(`- id: ${quoteYamlString(item.id || '')}`);
        appendYamlScalar(lines, 2, 'violation_category', item.violation_category || '');

        const rules = item.rules && item.rules.length > 0
            ? item.rules
            : (item.violation_check_prompt ||
                  item.forbidden_regular_expressions ||
                  item.musthave_regular_expressions ||
                  (Array.isArray((item as any).forbidden_image_assets) && (item as any).forbidden_image_assets.length > 0) ||
                  item.detail)
                ? [{
                    id: 'rule_1',
                    name: 'Rule 1',
                    type: item.type || 'description',
                    patrol_source: item.patrol_source,
                    detail: item.detail,
                    violation_check_prompt: item.violation_check_prompt,
                    existence_check_prompt: item.existence_check_prompt,
                    skip_existence_check: (item as any).skip_existence_check,
                    forbidden_regular_expressions: item.forbidden_regular_expressions,
                    forbidden_detect_message: item.forbidden_detect_message,
                    musthave_regular_expressions: item.musthave_regular_expressions,
                    musthave_not_detect_message: item.musthave_not_detect_message,
                    regular_expression_conditions: item.regular_expression_conditions,
                    context_window: item.context_window,
                    forbidden_image_assets: (item as any).forbidden_image_assets,
                    similarity_threshold: (item as any).similarity_threshold,
                    min_good_matches: (item as any).min_good_matches,
                    min_score: (item as any).min_score,
                    min_inlier_ratio: (item as any).min_inlier_ratio,
                }] as GuidelineRule[]
                : [];

        if (rules.length > 0) {
            lines.push('  rules:');
            rules.forEach((rule, ruleIndex) => {
                lines.push('    -');
                appendYamlScalar(lines, 6, 'name', rule.name || `Rule ${ruleIndex + 1}`);
                appendYamlScalar(lines, 6, 'type', rule.type);
                appendYamlScalar(lines, 6, 'patrol_source', rule.patrol_source);
                appendYamlScalar(lines, 6, 'detail', rule.detail);
                appendYamlScalar(lines, 6, 'violation_check_prompt', rule.violation_check_prompt);
                appendYamlScalar(lines, 6, 'existence_check_prompt', rule.existence_check_prompt);
                if (rule.skip_existence_check) {
                    lines.push(`${' '.repeat(6)}skip_existence_check: true`);
                }

                if (rule.type === 'forbidden_image') {
                    appendYamlNumberIfPresent(lines, 6, 'similarity_threshold', (rule as any).similarity_threshold);
                    appendYamlNumberIfPresent(lines, 6, 'min_good_matches', (rule as any).min_good_matches);
                    appendYamlNumberIfPresent(lines, 6, 'min_score', (rule as any).min_score);
                    appendYamlNumberIfPresent(lines, 6, 'min_inlier_ratio', (rule as any).min_inlier_ratio);
                    appendYamlImageAssets(lines, 6, 'forbidden_image_assets', ((rule as any).forbidden_image_assets || []) as any[]);
                }
                if (rule.type === 'required_image_match') {
                    appendYamlScalar(lines, 6, 'target_image_extraction_prompt', (rule as any).target_image_extraction_prompt);
                    appendYamlNumberIfPresent(lines, 6, 'min_good_matches', (rule as any).min_good_matches);
                    appendYamlNumberIfPresent(lines, 6, 'min_score', (rule as any).min_score);
                    appendYamlNumberIfPresent(lines, 6, 'min_inlier_ratio', (rule as any).min_inlier_ratio);
                    appendYamlImageAssets(lines, 6, 'required_image_assets', ((rule as any).required_image_assets || []) as any[]);
                }

                if (rule.forbidden_regular_expressions) {
                    appendYamlStringArray(lines, 6, 'forbidden_regular_expressions', rule.forbidden_regular_expressions);
                }
                appendYamlScalar(lines, 6, 'forbidden_detect_message', rule.forbidden_detect_message);

                if (rule.musthave_regular_expressions) {
                    appendYamlStringArray(lines, 6, 'musthave_regular_expressions', rule.musthave_regular_expressions);
                }
                appendYamlScalar(lines, 6, 'musthave_not_detect_message', rule.musthave_not_detect_message);

                if (rule.regular_expression_conditions && rule.regular_expression_conditions.length > 0) {
                    lines.push('      regular_expression_conditions:');
                    rule.regular_expression_conditions.forEach(condition => {
                        lines.push('        -');
                        appendYamlScalar(lines, 10, 'type', condition.type || 'allow_if');
                        if (condition.context_window !== undefined && condition.context_window !== null) {
                            lines.push(`          context_window: ${condition.context_window}`);
                        }
                        appendYamlScalar(lines, 10, 'rule', condition.rule);
                    });
                }
            });
        }

        lines.push('');
    });

    return lines.join('\n');
};

export default GuidelineManagementPage;

